import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { Textarea } from '@/components/ui/textarea';
import { cn, getLocalDateString } from '@/lib/utils';
import {
  Calendar,
  ChevronDown,
  ChevronRight,
  Download,
  FileSpreadsheet,
  Printer,
  IndianRupee,
  PlusCircle,
  Search,
  ArrowLeft,
  Pencil,
  Loader2,
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { downloadPdf } from '@/lib/pdfDownload';
import { format } from 'date-fns';
import { Capacitor } from '@capacitor/core';
import * as XLSX from 'xlsx';
import { useCompanyState } from '@/hooks/useCompanyState';
import { getBaseUrl } from '@/lib/api';
import { useBackNavigation } from '@/hooks/use-back-navigation';

type Product = {
  id: string;
  name: string;
  unitCost: number;
  qty: number;
  total: number;
};

type Payment = {
  id: string;
  paidOn: string; // Full datetime string including time
  paidAmount: number;
  receiptNo: string;
  type: string;
  invoiceNo: string;
};

type Invoice = {
  id: string;
  invoiceDate: string;
  companyName: string;
  invoiceNumber: string;
  invoiceAmount: number;
  location: string;
  profit: number;
  products: Product[];
  payments: Payment[];
};

type CustomerLedger = {
  id: string;
  customerCode?: string;
  name: string;
  buyer: string;
  phone: string;
  openingBalance: number;
  totalInvoiced: number;
  totalPaid: number;
  totalDue: number;
  dueSinceDays: number;
  invoices: Invoice[];
  serialNumber?: number;
};

type CustomerLedgerTabProps = {
  token?: string;
};

// Function to format datetime for display
const formatPaymentDateTime = (dateTimeStr: string) => {
  if (!dateTimeStr) return '';
  
  try {
    // If the string contains 'T', it's a full datetime
    if (dateTimeStr.includes('T')) {
      const date = new Date(dateTimeStr);
      return format(date, 'dd-MM-yyyy HH:mm'); // Format as DD-MM-YYYY HH:MM
    }
    
    // If it's just a date, return as-is
    return dateTimeStr;
  } catch (error) {
    console.error('Error formatting payment datetime:', error);
    return dateTimeStr;
  }
};

const currency = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

const getTodayStr = () => getLocalDateString();

const mockLedgerData: CustomerLedger[] = [
  {
    id: 'cust-1',
    name: 'Acme Retail Pvt Ltd',
    buyer: 'Arjun Mehta',
    phone: '+91 98765 43210',
    openingBalance: 12000,
    totalInvoiced: 215000,
    totalPaid: 165000,
    totalDue: 50000,
    dueSinceDays: 18,
    invoices: [
      {
        id: 'inv-101',
        invoiceDate: '2025-08-03',
        companyName: 'Acme Retail Pvt Ltd',
        invoiceNumber: 'INV-101',
        invoiceAmount: 125000,
        location: 'Hyderabad',
        profit: 18500,
        products: [
          { id: 'p1', name: 'Premium Rice (25kg)', unitCost: 3200, qty: 12, total: 38400 },
          { id: 'p2', name: 'Sunflower Oil (15L)', unitCost: 1650, qty: 20, total: 33000 },
          { id: 'p3', name: 'Basmati Rice (10kg)', unitCost: 980, qty: 18, total: 17640 },
        ],
        payments: [
          { id: 'pay1', paidOn: '2025-08-05', paidAmount: 40000, receiptNo: 'RCPT-7412', type: 'UPI', invoiceNo: 'INV-101' },
          { id: 'pay2', paidOn: '2025-08-10', paidAmount: 35000, receiptNo: 'RCPT-7413', type: 'NEFT', invoiceNo: 'INV-101' },
        ],
      },
      {
        id: 'inv-102',
        invoiceDate: '2025-07-26',
        companyName: 'Acme Retail Pvt Ltd',
        invoiceNumber: 'INV-102',
        invoiceAmount: 90000,
        location: 'Hyderabad',
        profit: 12400,
        products: [
          { id: 'p4', name: 'Organic Atta (10kg)', unitCost: 560, qty: 25, total: 14000 },
          { id: 'p5', name: 'Olive Oil (5L)', unitCost: 1150, qty: 16, total: 18400 },
        ],
        payments: [
          { id: 'pay3', paidOn: '2025-07-28', paidAmount: 30000, receiptNo: 'RCPT-7366', type: 'Cash', invoiceNo: 'INV-102' },
        ],
      },
    ],
  },
  {
    id: 'cust-2',
    name: 'BlueWave Supermarket',
    buyer: 'Sana Kulkarni',
    phone: '+91 98221 11880',
    openingBalance: 8000,
    totalInvoiced: 163500,
    totalPaid: 149000,
    totalDue: 14500,
    dueSinceDays: 7,
    invoices: [
      {
        id: 'inv-201',
        invoiceDate: '2025-08-02',
        companyName: 'BlueWave Supermarket',
        invoiceNumber: 'INV-201',
        invoiceAmount: 75500,
        location: 'Pune',
        profit: 9800,
        products: [
          { id: 'p6', name: 'Sona Masoori Rice (25kg)', unitCost: 2400, qty: 10, total: 24000 },
          { id: 'p7', name: 'Mustard Oil (1L)', unitCost: 170, qty: 60, total: 10200 },
        ],
        payments: [
          { id: 'pay4', paidOn: '2025-08-04', paidAmount: 25000, receiptNo: 'RCPT-7420', type: 'UPI', invoiceNo: 'INV-201' },
          { id: 'pay5', paidOn: '2025-08-08', paidAmount: 20000, receiptNo: 'RCPT-7425', type: 'Card', invoiceNo: 'INV-201' },
        ],
      },
      {
        id: 'inv-202',
        invoiceDate: '2025-07-20',
        companyName: 'BlueWave Supermarket',
        invoiceNumber: 'INV-202',
        invoiceAmount: 88000,
        location: 'Pune',
        profit: 11200,
        products: [
          { id: 'p8', name: 'Ready Meals Pack', unitCost: 420, qty: 80, total: 33600 },
          { id: 'p9', name: 'Soft Drinks Crate', unitCost: 520, qty: 40, total: 20800 },
        ],
        payments: [
          { id: 'pay6', paidOn: '2025-07-25', paidAmount: 28000, receiptNo: 'RCPT-7320', type: 'NEFT', invoiceNo: 'INV-202' },
          { id: 'pay7', paidOn: '2025-07-30', paidAmount: 25000, receiptNo: 'RCPT-7324', type: 'Cheque', invoiceNo: 'INV-202' },
        ],
      },
    ],
  },
];

type InvoiceProductsProps = {
  products: Product[];
  expanded: boolean;
  onToggle: () => void;
};

const InvoiceProducts = ({ products, expanded, onToggle }: InvoiceProductsProps) => (
  <div className="rounded-lg border border-dashed border-slate-200 bg-white">
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center justify-between px-3 sm:px-4 py-2.5 text-left focus:outline-none"
    >
      <div className="font-semibold text-gray-900">Products ({products.length})</div>
    </button>
    {expanded && (
      <div className="px-3 sm:px-4 pb-3 sm:pb-4 space-y-2">
        <div className="hidden sm:grid grid-cols-4 gap-3 text-xs font-semibold text-gray-500">
          <div>Product Name</div>
          <div className="text-right">Unit Cost</div>
          <div className="text-right">Qty</div>
          <div className="text-right">Total</div>
        </div>
        <div className="space-y-2">
          {products.map((product) => (
            <div
              key={product.id}
              className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 rounded-lg border border-slate-100 bg-slate-50/70 px-3 py-2 text-sm"
            >
              <div className="col-span-2 sm:col-span-1 font-medium text-gray-900">{product.name}</div>
              <div className="text-gray-700 sm:text-right">
                <span className="sm:hidden font-semibold text-gray-500">Unit Cost: </span>
                {currency.format(product.unitCost)}
              </div>
              <div className="text-gray-700 sm:text-right">
                <span className="sm:hidden font-semibold text-gray-500">Qty: </span>
                {product.qty}
              </div>
              <div className="text-gray-900 font-semibold sm:text-right">
                <span className="sm:hidden font-semibold text-gray-500">Total: </span>
                {currency.format(product.total)}
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

type InvoicePaymentsProps = {
  payments: Payment[];
  expanded: boolean;
  onToggle: () => void;
};

const InvoicePayments = ({ payments, expanded, onToggle }: InvoicePaymentsProps) => {
  // Sort payments by datetime in descending order (newest first)
  const sortedPayments = [...payments].sort((a, b) => {
    const dateA = new Date(a.paidOn || '1970-01-01');
    const dateB = new Date(b.paidOn || '1970-01-01');
    return dateB.getTime() - dateA.getTime();
  });

  return (
  <div className="rounded-lg border border-dashed border-slate-200 bg-white">
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center gap-2 px-3 sm:px-4 py-2.5 text-left focus:outline-none"
    >
      <ChevronDown
        className={cn('h-4 w-4 text-gray-500 transition-transform', expanded && 'rotate-180')}
      />
      <div className="font-semibold text-gray-900">Payments ({payments.length})</div>
    </button>
    {expanded && (
      <div className="px-3 sm:px-4 pb-3 sm:pb-4 space-y-2">
        <div className="hidden sm:grid grid-cols-5 gap-3 text-xs font-semibold text-gray-500">
          <div>Paid On</div>
          <div className="text-left">Paid Amount</div>
          <div className="text-left">Receipt No</div>
          <div className="text-left">Type</div>
          <div className="text-left">Invoice No</div>
        </div>
        <div className="space-y-2">
          {sortedPayments.map((payment) => (
            <div
              key={payment.id}
              className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-3 rounded-lg border border-slate-100 bg-slate-50/70 px-3 py-2 text-sm"
            >
              <div className="font-medium text-gray-900">{formatPaymentDateTime(payment.paidOn)}</div>
              <div className="text-gray-900 font-semibold sm:text-left">
                <span className="sm:hidden font-semibold text-gray-500">Paid Amount: </span>
                {currency.format(payment.paidAmount)}
              </div>
              <div className="text-gray-700 sm:text-left">
                <span className="sm:hidden font-semibold text-gray-500">Receipt No: </span>
                {payment.receiptNo}
              </div>
              <div className="text-gray-700 sm:text-left">
                <span className="sm:hidden font-semibold text-gray-500">Type: </span>
                {payment.type}
              </div>
              <div className="text-gray-700 sm:text-left">
                <span className="sm:hidden font-semibold text-gray-500">Invoice No: </span>
                {payment.invoiceNo}
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
  );
};

type InvoiceRowProps = {
  invoice: Invoice;
  expanded: boolean;
  onToggle: () => void;
};

const InvoiceRow = ({
  invoice,
  expanded,
  onToggle,
}: InvoiceRowProps) => (
  <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
    <button
      onClick={onToggle}
      className="w-full flex flex-col gap-3 px-3 sm:px-4 py-3 text-left hover:bg-slate-50/80 transition-colors"
    >
      <div className="hidden lg:grid grid-cols-[32px,1.2fr,1.2fr,1fr,1fr,1fr,1fr] items-center gap-4 text-sm text-gray-700">
        <ChevronDown
          className={cn('h-4 w-4 transition-transform text-gray-500', expanded && 'rotate-180')}
        />
        <div className="font-semibold text-gray-900">{invoice.invoiceDate}</div>
        <div className="text-gray-800">{invoice.companyName}</div>
        <div className="font-medium text-gray-900 text-right">{invoice.invoiceNumber}</div>
        <div className="text-right font-semibold text-gray-900">{currency.format(invoice.invoiceAmount)}</div>
        <div className="text-gray-800 text-center">{invoice.location}</div>
        <div className="text-right text-emerald-600 font-semibold">{currency.format(invoice.profit)}</div>
      </div>

      <div className="lg:hidden space-y-2 text-sm text-gray-700">
        <div className="flex items-center justify-between">
          <div className="font-semibold text-gray-900">{invoice.companyName}</div>
          <ChevronRight
            className={cn('h-4 w-4 text-gray-500 transition-transform', expanded && 'rotate-90')}
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <p className="text-xs text-gray-500">Invoice Date</p>
            <p className="font-medium text-gray-900">{invoice.invoiceDate}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Invoice No</p>
            <p className="font-semibold text-gray-900">{invoice.invoiceNumber}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Location</p>
            <p className="font-medium text-gray-900">{invoice.location}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Invoice Amount</p>
            <p className="font-semibold text-gray-900">{currency.format(invoice.invoiceAmount)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Profit</p>
            <p className="font-semibold text-emerald-600">{currency.format(invoice.profit)}</p>
          </div>
        </div>
      </div>
    </button>

    {expanded && (
      <div className="border-t border-slate-100 bg-slate-50/70 px-3 sm:px-4 py-3">
        <InvoiceProducts products={invoice.products} expanded={true} onToggle={() => {}} />
      </div>
    )}
  </div>
);

type CustomerRowProps = {
  customer: CustomerLedger;
  expanded: boolean;
  onToggle: () => void;
  expandedInvoices: Record<string, boolean>;
  toggleInvoice: (id: string) => void;
  paymentsExpanded: boolean;
  togglePayments: () => void;
  onOpeningBalanceEdit: (customer: CustomerLedger) => void;
  selected: boolean;
  onSelectToggle: () => void;
  invoices: Invoice[];
  invoicesLoading?: boolean;
};

const CustomerRow = ({
  customer,
  expanded,
  onToggle,
  expandedInvoices,
  toggleInvoice,
  paymentsExpanded,
  togglePayments,
  onOpeningBalanceEdit,
  selected,
  onSelectToggle,
  invoices,
  invoicesLoading,
}: CustomerRowProps) => (
  <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex flex-col gap-3 px-3 sm:px-4 py-3 text-left hover:bg-slate-50 transition-colors"
    >
      <div className="hidden lg:grid grid-cols-[32px,32px,60px,1.4fr,1fr,1fr,1fr,1fr,1fr,1fr,1fr] items-center gap-3 text-sm text-gray-700">
        <ChevronDown
          className={cn('h-5 w-5 text-gray-500 transition-transform', expanded && 'rotate-180')}
        />
        <input
          type="checkbox"
          checked={selected}
          onChange={(e) => {
            e.stopPropagation();
            onSelectToggle();
          }}
          onClick={(e) => e.stopPropagation()}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <div className="text-center font-medium text-gray-700">{customer.serialNumber || ''}</div>
        <div className="font-semibold text-gray-900">{customer.name}</div>
        <div className="text-gray-800">{customer.buyer}</div>
        <div className="text-gray-800">{customer.phone}</div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpeningBalanceEdit(customer);
          }}
          className="flex items-center justify-end gap-1 text-right text-gray-900 font-semibold hover:text-blue-700 focus:outline-none"
        >
          <span>{currency.format(customer.openingBalance)}</span>
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-600 hover:text-gray-900">
            <Pencil className="h-4 w-4" />
          </span>
        </button>
        <div className="text-right text-gray-900 font-semibold">{currency.format(customer.totalInvoiced)}</div>
        <div className="text-right text-emerald-600 font-semibold">{currency.format(customer.totalPaid)}</div>
        <div className="text-right text-amber-600 font-semibold">{currency.format(customer.totalDue)}</div>
        <div className="text-right text-gray-800">{customer.dueSinceDays} days</div>
      </div>

      <div className="lg:hidden space-y-2 text-sm text-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={selected}
              onChange={(e) => {
                e.stopPropagation();
                onSelectToggle();
              }}
              onClick={(e) => e.stopPropagation()}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-700 text-sm min-w-[30px]">#{customer.serialNumber || ''}</span>
              <div>
                <p className="text-xs text-gray-500">Name</p>
                <p className="font-semibold text-gray-900">{customer.name}</p>
                <p className="text-xs text-gray-500">Company: {customer.buyer}</p>
              </div>
            </div>
          </div>
          <ChevronRight
            className={cn('h-4 w-4 text-gray-500 transition-transform', expanded && 'rotate-90')}
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <p className="text-xs text-gray-500">Phone</p>
            <p className="font-medium text-gray-900">{customer.phone}</p>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpeningBalanceEdit(customer);
            }}
            className="flex items-center gap-1 text-left"
          >
            <div>
              <p className="text-xs text-gray-500">Opening Balance</p>
              <p className="font-semibold text-gray-900">{currency.format(customer.openingBalance)}</p>
            </div>
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-600 hover:text-gray-900">
              <Pencil className="h-4 w-4" />
            </span>
          </button>
          <div>
            <p className="text-xs text-gray-500">Total Invoiced</p>
            <p className="font-semibold text-gray-900">{currency.format(customer.totalInvoiced)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Total Paid</p>
            <p className="font-semibold text-emerald-600">{currency.format(customer.totalPaid)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Total Due</p>
            <p className="font-semibold text-amber-600">{currency.format(customer.totalDue)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Due Since</p>
            <p className="font-medium text-gray-900">{customer.dueSinceDays} days</p>
          </div>
        </div>
      </div>
    </button>

    {expanded && (
      <div className="border-t border-slate-100 bg-slate-50/70 px-3 sm:px-4 py-4 space-y-4">
        <div className="space-y-2">
          <div className="text-sm font-semibold text-gray-900 px-1">Invoices</div>
        {invoicesLoading ? (
          <div className="text-sm text-gray-500 px-1">Loading invoices...</div>
        ) : (
          <div className="space-y-3">
            {invoices.length > 0 && (
              <div className="hidden lg:grid grid-cols-[32px,1.2fr,1.2fr,1fr,1fr,1fr,1fr] items-center gap-4 px-3 sm:px-4 text-xs font-semibold text-gray-500">
                <div />
                <div>Invoice Date</div>
                <div>Company Name</div>
                <div className="text-right">Invoice Number</div>
                <div className="text-right">Invoice Amount</div>
                <div className="text-center">Location</div>
                <div className="text-right">Profit</div>
              </div>
            )}
            {invoices.map((invoice) => (
              <InvoiceRow
                key={invoice.id}
                invoice={invoice}
                expanded={!!expandedInvoices[invoice.id]}
                onToggle={() => toggleInvoice(invoice.id)}
              />
            ))}
            {invoices.length === 0 && (
              <div className="text-sm text-gray-500 px-1">No invoices found.</div>
            )}
          </div>
        )}
        </div>

        <div className="space-y-2">
          <div className="text-sm font-semibold text-gray-900 px-1">Payments</div>
          <InvoicePayments
            payments={invoices.flatMap((inv) => inv.payments)}
            expanded={paymentsExpanded}
            onToggle={togglePayments}
          />
        </div>
      </div>
    )}
  </div>
);

const sizeOptions = ['10', '25', '50', '100', '200', 'all'];

const matchesDateRange = (date: string, start?: string, end?: string, enabled = false) => {
  if (!enabled) return true;
  const current = new Date(date);
  if (start) {
    const startDate = new Date(start);
    if (current < startDate) return false;
  }
  if (end) {
    const endDate = new Date(end);
    if (current > endDate) return false;
  }
  return true;
};

const CustomerLedgerTab: React.FC<CustomerLedgerTabProps> = ({ token }) => {
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const { company } = useCompanyState();
  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState<string>('25');
  const [dateRange, setDateRange] = useState<{ start?: string; end?: string }>({
    start: getTodayStr(),
    end: getTodayStr(),
  });
  const [customersReloadKey, setCustomersReloadKey] = useState(0);
  const [customers, setCustomers] = useState<CustomerLedger[]>([]);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [invoicesByCustomer, setInvoicesByCustomer] = useState<Record<string, Invoice[]>>({});
  const [invoicesLoading, setInvoicesLoading] = useState<Record<string, boolean>>({});
  const [expandedCustomers, setExpandedCustomers] = useState<Record<string, boolean>>({});
  const [expandedInvoices, setExpandedInvoices] = useState<Record<string, boolean>>({});
  const [expandedPayments, setExpandedPayments] = useState<Record<string, boolean>>({});
  const [isFilterCollapsed, setIsFilterCollapsed] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [dateFilterEnabled, setDateFilterEnabled] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const mobileStartRef = useRef<HTMLInputElement | null>(null);
  const mobileEndRef = useRef<HTMLInputElement | null>(null);
  const fetchIdRef = useRef(0);
  const invoicesFetchRef = useRef<Record<string, number>>({});
  const prevDateRangeKey = useRef<string>(`${getTodayStr()}|${getTodayStr()}`);
  const [isObModalOpen, setIsObModalOpen] = useState(false);
  const [obSubmitting, setObSubmitting] = useState(false);
  const [obForm, setObForm] = useState<{
    customerId?: string;
    customerCode?: string;
    openingBalance: string;
    customerName?: string;
  }>({ openingBalance: '0' });
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [companyOptions, setCompanyOptions] = useState<string[]>([]);
  const [companyMap, setCompanyMap] = useState<Record<string, string>>({});
  const [companyLoading, setCompanyLoading] = useState(false);
  const [bankData, setBankData] = useState<Array<{ bank: string; accounts: string[] }>>([]);
  const [bankLoading, setBankLoading] = useState(false);
  const [paymentForm, setPaymentForm] = useState<{
    customerId?: string;
    customerCode?: string;
    customerName?: string;
    totalDue: number;
    companySearch: string;
    companySelected?: string;
    paymentReceived: string;
    receivedOn: string;
    reference: string;
    linkInvoice: boolean;
    invoiceSearch: string;
    invoiceSelected?: string;
    notes: string;
    paymentMethod: string;
    bankName?: string;
    accountNumber?: string;
    selectedInvoiceId?: string;
    selectedInvoiceCompanyId?: number;
  }>({
    totalDue: 0,
    companySearch: '',
    paymentReceived: '',
    receivedOn: getTodayStr(),
    reference: '',
    linkInvoice: false,
    invoiceSearch: '',
    notes: '',
    paymentMethod: 'Cash',
  });
  const [invoiceOptions, setInvoiceOptions] = useState<
    Array<{ invSeqNo: string; invoiceId: string; companyId?: number }>
  >([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm.trim()), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Handle search term passed from SalesInvoice navigation
  useEffect(() => {
    if (location.state?.searchTerm) {
      setSearchTerm(location.state.searchTerm);
      // Clear the location state to prevent re-setting on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (event: MediaQueryListEvent) => {
      setIsDesktop(event.matches);
      setIsFilterCollapsed(false); // Expanded by default on all screens
    };

    setIsDesktop(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const handleBackToReports = useCallback(() => {
    navigate('/?tab=reports');
  }, [navigate]);

  useBackNavigation({
    onBack: handleBackToReports,
    canGoBack: true,
    showExitConfirmation: false,
  });

  useEffect(() => {
    if (!isObModalOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsObModalOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isObModalOpen]);

  const openObModal = (customer: CustomerLedger) => {
    setObForm({
      customerId: customer.id,
      customerCode: customer.customerCode,
      openingBalance: Number.isFinite(customer.openingBalance)
        ? String(customer.openingBalance)
        : '0',
      customerName: customer.name,
    });
    setIsObModalOpen(true);
  };

  const closeObModal = () => {
    if (obSubmitting) return;
    setIsObModalOpen(false);
    setObForm({ openingBalance: '0' });
  };

  const handleSubmitOb = async () => {
    if (!obForm.customerId || (!obForm.customerCode && !obForm.customerId)) return;
    const value = Number(obForm.openingBalance);
    if (!Number.isFinite(value) || value < 0) {
      toast({
        variant: 'destructive',
        description: 'Opening balance must be zero or a positive number',
      });
      return;
    }

    const baseUrl = getBaseUrl();
    const rawToken = token || localStorage.getItem('token') || '';
    const authToken = rawToken ? (rawToken.startsWith('Bearer ') ? rawToken : `Bearer ${rawToken}`) : '';
    const endpoint = `${baseUrl}/api/UpdateOB`;
    const body = { CustomerCode: obForm.customerCode ?? obForm.customerId, OpeningBalance: value };

    console.log('🛰️ UpdateOB request:', endpoint, body);
    setObSubmitting(true);

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          ...(authToken ? { Authorization: authToken } : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        console.error('UpdateOB error status:', response.status, response.statusText);
        throw new Error('Failed to update opening balance');
      }

      const json = await response.json();
      console.log('🛰️ UpdateOB response:', json);

      if (json.status === 0) {
        toast({ description: 'Opening balance updated successfully' });
        setCustomers((prev) =>
          prev.map((c) =>
            c.id === obForm.customerId ? { ...c, openingBalance: value } : c,
          ),
        );
        setCustomersReloadKey((prev) => prev + 1);
        closeObModal();
      } else {
        toast({ variant: 'destructive', description: 'Error updating opening balance' });
      }
    } catch (error) {
      console.error('UpdateOB fetch failed:', error);
      toast({ variant: 'destructive', description: 'Error updating opening balance' });
    } finally {
      setObSubmitting(false);
    }
  };

  const fetchCompanies = useCallback(async () => {
    if (companyLoading) return;
    const baseUrl = getBaseUrl();
    const rawToken = token || localStorage.getItem('token') || '';
    const authToken = rawToken ? (rawToken.startsWith('Bearer ') ? rawToken : `Bearer ${rawToken}`) : '';
    const endpoint = `${baseUrl}/api/Companies`;
    console.log('🛰️ Companies request:', endpoint);
    setCompanyLoading(true);
    try {
      const response = await fetch(endpoint, {
        headers: {
          ...(authToken ? { Authorization: authToken } : {}),
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        console.error('Companies error status:', response.status, response.statusText);
        throw new Error('Failed to fetch companies');
      }
      const json = await response.json();
      console.log('🛰️ Companies response:', json);
      const list: string[] = [];
      const map: Record<string, string> = {};

      const companiesArray = Array.isArray(json?.data)
        ? json.data
        : Array.isArray(json)
        ? json
        : [];

      companiesArray.forEach((c: any) => {
        const name = (c.Name || c.CompanyName || c.name || c.FName || '').trim();
        const id = c.Id ?? c.CompanyID ?? c.id;
        if (name && id != null) {
          const idStr = String(id);
          list.push(name);
          map[name] = idStr;
        }
      });

      setCompanyOptions(list);
      setCompanyMap(map);
    } catch (error) {
      console.error('Companies fetch failed:', error);
      toast({ variant: 'destructive', description: 'Unable to load companies, showing defaults' });
      setCompanyOptions([]);
      setCompanyMap({});
    } finally {
      setCompanyLoading(false);
    }
  }, [companyLoading, token, toast]);

  const bankFetchedRef = useRef(false);

  const fetchBanks = useCallback(async () => {
    if (bankLoading || bankFetchedRef.current) return;
    const baseUrl = getBaseUrl();
    const rawToken = token || localStorage.getItem('token') || '';
    const authToken = rawToken ? (rawToken.startsWith('Bearer ') ? rawToken : `Bearer ${rawToken}`) : '';
    const endpoint = `${baseUrl}/api/GetBank`;
    console.log('🛰️ GetBank request:', endpoint);
    setBankLoading(true);
    bankFetchedRef.current = true;
    try {
      const response = await fetch(endpoint, {
        headers: {
          ...(authToken ? { Authorization: authToken } : {}),
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        console.error('GetBank error status:', response.status, response.statusText);
        throw new Error('Failed to fetch banks');
      }
      const json = await response.json();
      console.log('🛰️ GetBank response:', json);
      // Parse the response - group by BankName and collect AccountNo
      const bankMap = new Map<string, string[]>();
      if (Array.isArray(json?.data)) {
        json.data.forEach((b: any) => {
          const bankName = b.BankName || '';
          const accountNo = b.AccountNo ? String(b.AccountNo) : '';
          if (bankName && accountNo) {
            if (!bankMap.has(bankName)) {
              bankMap.set(bankName, []);
            }
            bankMap.get(bankName)!.push(accountNo);
          }
        });
      }
      const banks: Array<{ bank: string; accounts: string[] }> = Array.from(bankMap.entries()).map(
        ([bank, accounts]) => ({ bank, accounts }),
      );
      setBankData(banks);
      // Set default values if available
      if (banks.length > 0) {
        const firstBank = banks[0];
        setPaymentForm((prev) => ({
          ...prev,
          bankName: firstBank.bank,
          accountNumber: firstBank.accounts[0] || '',
        }));
      }
    } catch (error) {
      console.error('GetBank fetch failed:', error);
      toast({ variant: 'destructive', description: 'Unable to load banks' });
      setBankData([]);
      bankFetchedRef.current = false;
    } finally {
      setBankLoading(false);
    }
  }, [bankLoading, token, toast]);

  const openPaymentModal = () => {
    if (selectedCustomers.length === 0) {
      toast({ variant: 'destructive', description: 'Please select a customer first' });
      return;
    }
    const customerId = selectedCustomers[0];
    const customer = customers.find((c) => c.id === customerId);
    if (!customer) {
      toast({ variant: 'destructive', description: 'Customer not found' });
      return;
    }

    setPaymentForm((prev) => ({
      ...prev,
      customerId,
      customerCode: customer.customerCode,
      customerName: customer.name,
      totalDue: customer.totalDue ?? 0,
      companySelected: companyOptions[0] || '',
      companySearch: companyOptions[0] || '',
      paymentReceived: '',
      receivedOn: getTodayStr(),
      reference: '',
      linkInvoice: false,
      invoiceSearch: '',
      invoiceSelected: '',
      selectedInvoiceId: undefined,
      selectedInvoiceCompanyId: undefined,
      notes: '',
      paymentMethod: 'Cash',
      bankName: undefined,
      accountNumber: undefined,
    }));
    setInvoiceOptions([]);
    bankFetchedRef.current = false;
    setIsPaymentModalOpen(true);
    
    // Call all three APIs when opening the payment modal
    fetchCompanies();
    fetchBanks();
    fetchCustomerDueInvoices(customer.customerCode);
  };

  const closePaymentModal = () => {
    if (paymentSubmitting) return;
    setIsPaymentModalOpen(false);
    bankFetchedRef.current = false;
  };

  useEffect(() => {
    if (isPaymentModalOpen && !companyOptions.length && !companyLoading) {
      fetchCompanies();
    }
  }, [isPaymentModalOpen, companyOptions.length, companyLoading, fetchCompanies]);

  useEffect(() => {
    if (!isPaymentModalOpen) return;
    if (!paymentForm.companySelected && companyOptions.length) {
      const first = companyOptions[0];
      setPaymentForm((prev) => ({
        ...prev,
        companySelected: first,
        companySearch: first,
      }));
    }
  }, [isPaymentModalOpen, companyOptions, paymentForm.companySelected]);

  useEffect(() => {
    if (paymentForm.paymentMethod === 'Bank Transfer' && !bankFetchedRef.current && !bankLoading) {
      fetchBanks();
    }
  }, [paymentForm.paymentMethod, bankLoading, fetchBanks]);

  const filteredInvoices = useMemo(() => {
    if (!paymentForm.linkInvoice || !invoiceOptions.length) return [];
    const search = paymentForm.invoiceSearch.trim().toLowerCase();
    return invoiceOptions
      .filter((opt) => !search || opt.invSeqNo.toLowerCase().includes(search))
      .map((opt) => opt.invSeqNo);
  }, [paymentForm.linkInvoice, paymentForm.invoiceSearch, invoiceOptions]);

  const availableAccounts = useMemo(() => {
    // Always show all accounts so user can select any account and auto-select the bank
    return bankData.flatMap((b) => b.accounts.map((acc) => ({ account: acc, bank: b.bank })));
  }, [bankData]);

  const updateBankFromAccount = (account: string) => {
    const found = bankData.find((b) => b.accounts.includes(account));
    if (found) {
      setPaymentForm((prev) => ({ ...prev, accountNumber: account, bankName: found.bank }));
    } else {
      setPaymentForm((prev) => ({ ...prev, accountNumber: account }));
    }
  };

  const updateAccountFromBank = (bank: string) => {
    const found = bankData.find((b) => b.bank === bank);
    const firstAccount = found?.accounts[0] || '';
    setPaymentForm((prev) => ({
      ...prev,
      bankName: bank || undefined,
      accountNumber: firstAccount, // Auto-select first account when bank is selected
    }));
  };

  const remainingDue = useMemo(() => {
    const received = Number(paymentForm.paymentReceived) || 0;
    if (!Number.isFinite(received)) return paymentForm.totalDue;
    return paymentForm.totalDue - received;
  }, [paymentForm.paymentReceived, paymentForm.totalDue]);

  const getPaymentMethodFromStatus = (status: number): string => {
    switch (status) {
      case 1:
        return 'Cash';
      case 2:
        return 'Card';
      case 3:
        return 'Wallet';
      case 4:
        return 'Coupon';
      case 5:
        return 'Credit Note';
      case 6:
        return 'Cheque';
      case 7:
        return 'Bank Transfer';
      default:
        return 'Unknown';
    }
  };

  const getPaymentModeCode = (method: string): string => {
    switch (method) {
      case 'Cash':
        return 'cash';
      case 'Card':
        return 'card';
      case 'Wallet':
        return 'wallet';
      case 'Coupon':
        return 'coupon';
      case 'Credit Note':
        return 'credit note';
      case 'Cheque':
        return 'cheque';
      case 'Bank Transfer':
        return 'bank';
      default:
        return 'cash';
    }
  };

  const handleOverallPDF = useCallback(async () => {
    try {
      // Check if any customers are selected
      if (selectedCustomers.length === 0) {
        toast({
          title: "No Selection",
          description: "Please select at least one customer to generate the PDF",
          variant: "destructive"
        });
        return;
      }

      toast({
        title: "Generating PDF...",
        description: "Preparing overall customer ledger report",
      });

      const baseUrl = getBaseUrl();
      const rawToken = token || localStorage.getItem('token') || '';
      const authToken = rawToken ? (rawToken.startsWith('Bearer ') ? rawToken : `Bearer ${rawToken}`) : '';

      const companyName = (company?.Name || (company as any)?.CompanyName || (company as any)?.name || '').trim();

      // Get selected customer codes from the current customers list
      const selectedCustomerCodes = customers
        .filter(c => selectedCustomers.includes(c.id))
        .map(c => c.customerCode || c.id);

      // Always fetch all customers to ensure we have all selected customer data
      const endpoint = `${baseUrl}/api/CustomerList?columnname=fname&direction=DESC&offset=0&limit=1&searchstr=${encodeURIComponent(debouncedSearchTerm)}&filter=all`;

      const response = await fetch(endpoint, {
        headers: {
          ...(authToken ? { Authorization: authToken } : {}),
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch customer data');
      }

      const json = await response.json();
      let customerData = json.data || [];

      // Filter to only selected customers
      if (selectedCustomerCodes.length > 0) {
        customerData = customerData.filter((c: any) => 
          selectedCustomerCodes.includes(String(c.Customercode))
        );
      }

      // Format date as "Saturday 20-12-2025"
      const today = new Date();
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dayName = dayNames[today.getDay()];
      const formattedDate = format(today, 'dd-MM-yyyy');
      const dateString = `${dayName} ${formattedDate}`;

      // Helper function to format numbers
      const formatNumber = (num: number): string => {
        return num.toLocaleString('en-IN', { maximumFractionDigits: 2 });
      };

      // Helper function to round numbers (for summary)
      const roundNumber = (num: number): string => {
        return Math.round(num).toLocaleString('en-IN');
      };

      // Create PDF with white background
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'A4' });

      // Get page width for centering
      const pageWidth = doc.internal.pageSize.getWidth();

      // Add logo placeholder box at top left - separate small rectangle
      const logoBoxWidth = 40; // Width of the logo box in points
      const logoBoxHeight = 30; // Height of the logo box in points (smaller rectangle)
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.5);
      doc.rect(40, 15, logoBoxWidth, logoBoxHeight); // x, y, width, height - separate small rectangle at top left

      // Add header - centered, bold; only draw company name when present; date position adjusts dynamically
      let headerY = 30;
      if (companyName) {
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        const companyNameWidth = doc.getTextWidth(companyName);
        doc.text(companyName, (pageWidth - companyNameWidth) / 2, headerY);
        headerY += 20;
      }
      doc.setFontSize(14);
      doc.setFont('helvetica', 'normal');
      const dateStringWidth = doc.getTextWidth(dateString);
      doc.text(dateString, (pageWidth - dateStringWidth) / 2, headerY);

      // Prepare table data - Name: FName, Buyer: LName
      const tableData = customerData.map((c: any) => {
        return [
          c.FName || '',
          c.LName || '',
          c.Phone || '',
          formatNumber(c.OpeningBalance ?? 0),
          formatNumber(c['Total Invoiced Amount'] ?? 0),
          formatNumber(c['Total Paid Amount'] ?? 0),
          formatNumber(c['Total Due Amount'] ?? 0),
          (c['Number of days till today'] ?? 0).toString(),
        ];
      });

      // Calculate totals
      const totals = customerData.reduce(
        (acc: any, c: any) => ({
          openingBalance: acc.openingBalance + (c.OpeningBalance ?? 0),
          totalInvoiced: acc.totalInvoiced + (c['Total Invoiced Amount'] ?? 0),
          totalPaid: acc.totalPaid + (c['Total Paid Amount'] ?? 0),
          totalDue: acc.totalDue + (c['Total Due Amount'] ?? 0),
        }),
        { openingBalance: 0, totalInvoiced: 0, totalPaid: 0, totalDue: 0 }
      );

      // Add main table - optimized to fit at least 30 rows per page
      autoTable(doc, {
        startY: 60,
        head: [['Name', 'Buyer', 'Phone', 'Opening Balance', 'Total Invoiced Amount', 'Total Paid Amount', 'Total Due Amount', 'Due Since Days']],
        body: tableData,
        theme: 'plain',
        styles: {
          fontSize: 9,
          cellPadding: 2,
          halign: 'left',
          valign: 'middle',
          textColor: [0, 0, 0],
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          fontSize: 13,
          halign: 'center',
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
          cellPadding: 2,
        },
        bodyStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          halign: 'left',
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
        },
        columnStyles: {
          0: { halign: 'left' }, // Name
          1: { halign: 'left' }, // Buyer
          2: { halign: 'left' }, // Phone
          3: { halign: 'left' }, // Opening Balance
          4: { halign: 'left' }, // Total Invoiced Amount
          5: { halign: 'left' }, // Total Paid Amount
          6: { halign: 'left' }, // Total Due Amount
          7: { halign: 'left' }, // Due Since Days
        },
        margin: { left: 40, right: 40, top: 60 },
        tableLineColor: [0, 0, 0],
        tableLineWidth: 0.1,
        showHead: 'firstPage',
        didDrawPage: (data: any) => {
          if (data.pageNumber > 1) {
            const pw = doc.internal.pageSize.getWidth();
            let hy = 30;
            if (companyName) {
              doc.setFontSize(16);
              doc.setFont('helvetica', 'bold');
              doc.text(companyName, (pw - doc.getTextWidth(companyName)) / 2, hy);
              hy += 20;
            }
            doc.setFontSize(14);
            doc.setFont('helvetica', 'normal');
            doc.text(dateString, (pw - doc.getTextWidth(dateString)) / 2, hy);
          }

          // Add page number at bottom left
          const pageCount = doc.getNumberOfPages();
          const pageHeight = doc.internal.pageSize.getHeight();
          doc.setFontSize(9);
          doc.setFont('helvetica', 'normal');
          doc.text(`Page ${data.pageNumber} of ${pageCount}`, 40, pageHeight - 20);
        },
      });

      // Get the final Y position after the main table
      const finalY = (doc as any).lastAutoTable.finalY || 55;

      // Add summary table - clean white paper style with rounded values
      autoTable(doc, {
        startY: finalY + 10,
        head: [['Opening Balance', 'Total Invoiced Amount', 'Total Paid Amount', 'Total Due Amount']],
        body: [[
          roundNumber(totals.openingBalance),
          roundNumber(totals.totalInvoiced),
          roundNumber(totals.totalPaid),
          roundNumber(totals.totalDue),
        ]],
        theme: 'plain',
        styles: {
          fontSize: 9,
          cellPadding: 2,
          halign: 'left',
          valign: 'middle',
          textColor: [0, 0, 0],
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          fontSize: 13,
          halign: 'center',
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
          cellPadding: 2,
        },
        bodyStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          halign: 'left',
          fontStyle: 'normal',
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
        },
        margin: { left: 40, right: 40, top: 60 },
        tableLineColor: [0, 0, 0],
        tableLineWidth: 0.1,
        didDrawPage: (data: any) => {
          if (data.pageNumber > 1) {
            const pw = doc.internal.pageSize.getWidth();
            let hy = 30;
            if (companyName) {
              doc.setFontSize(16);
              doc.setFont('helvetica', 'bold');
              doc.text(companyName, (pw - doc.getTextWidth(companyName)) / 2, hy);
              hy += 20;
            }
            doc.setFontSize(14);
            doc.setFont('helvetica', 'normal');
            doc.text(dateString, (pw - doc.getTextWidth(dateString)) / 2, hy);
          }
          const pageCount = doc.getNumberOfPages();
          const pageHeight = doc.internal.pageSize.getHeight();
          doc.setFontSize(9);
          doc.setFont('helvetica', 'normal');
          doc.text(`Page ${data.pageNumber} of ${pageCount}`, 40, pageHeight - 20);
        },
      });

      // Download PDF
      const pdfDataUri = doc.output('datauristring');
      downloadPdf(
        pdfDataUri,
        'CustomerLedgerOverall',
        () => {
          toast({
            title: "Export Success",
            description: Capacitor.isNativePlatform()
              ? "PDF saved to device"
              : "Overall customer ledger PDF exported successfully",
            className: 'bg-green-600 text-white border-green-700'
          });
        },
        (error) => {
          toast({
            title: "Export Error",
            description: error,
            variant: "destructive"
          });
        }
      );
    } catch (error) {
      console.error('Overall PDF export error:', error);
      toast({
        title: "Export Error",
        description: error instanceof Error ? error.message : "Failed to export overall customer ledger",
        variant: "destructive"
      });
    }
  }, [selectedCustomers, customers, debouncedSearchTerm, token, toast, company]);

  const handleDetailedLedgerPDF = useCallback(async () => {
  try {
    if (selectedCustomers.length !== 1) {
      toast({
        title: "Invalid Selection",
        description: "Please select exactly one customer to generate the detailed ledger",
        variant: "destructive"
      });
      return;
    }

    const customerId = selectedCustomers[0];
    const customer = customers.find((c) => c.id === customerId);
    if (!customer) {
      toast({
        title: "Customer Not Found",
        description: "Selected customer not found",
        variant: "destructive"
      });
      return;
    }

    toast({
      title: "Generating PDF...",
      description: "Preparing detailed customer ledger report",
    });

    const baseUrl = getBaseUrl();
    const rawToken = token || localStorage.getItem('token') || '';
    const authToken = rawToken ? (rawToken.startsWith('Bearer ') ? rawToken : `Bearer ${rawToken}`) : '';

    const startDate = dateRange.start || getTodayStr();
    const endDate = dateRange.end || getTodayStr();

    const formatDateForAPI = (dateStr: string) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toISOString().split('T')[0];
    };

    const customerCode = customer.customerCode || customer.id;
    const endpoint = `${baseUrl}/api/CustInvoicesPayments?CustomerCode=${customerCode}&StartDate=${formatDateForAPI(startDate)}&EndDate=${formatDateForAPI(endDate)}`;

    const response = await fetch(endpoint, {
      headers: {
        ...(authToken ? { Authorization: authToken } : {}),
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch customer ledger data');
    }

    const json = await response.json();
    const invoices = json.Invoices || [];
    const openingBalance = json.OpeningBalance ?? 0;
    const closingBalance = json.ClosingBalance ?? 0;

    const companyName = (company?.Name || (company as any)?.CompanyName || (company as any)?.name || '').trim();
    const addrParts1 = [company?.Address1, (company as any)?.Address2, (company as any)?.Address3].filter(Boolean) as string[];
    const companyAddressLine1 = addrParts1.length ? addrParts1.join(', ').trim() : '';
    const addrParts2 = [company?.City, company?.State, (company as any)?.PinZip].filter(Boolean) as string[];
    const companyAddressLine2 = addrParts2.length ? addrParts2.join(', ').trim() : '';
    const phone = ((company as any)?.LandPhone || (company as any)?.Mobile || '').trim();
    const gst = ((company as any)?.GSTN || '').trim();
    const companyContactLine = [phone && `Phone : ${phone}`, gst && `GST : ${gst}`].filter(Boolean).join(', ') || '';

    const customerName = customer.name || (invoices.length > 0 && invoices[0].Invoice?.CustomerName) || '';
    const customerNumber = customer.phone || (invoices.length > 0 && invoices[0].Invoice?.Mobile1) || '';

    // Format date as DD-MMM-YYYY (e.g., 02-Dec-2025)
    const formatDisplayDate = (dateStr: string) => {
      if (!dateStr) return '';
      try {
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, '0');
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      } catch {
        return dateStr;
      }
    };

    const startDateDisplay = formatDisplayDate(startDate);
    const endDateDisplay = formatDisplayDate(endDate);

    // Format numbers without decimals for display
    const formatNumber = (num: number): string => {
      return Math.round(num).toLocaleString('en-IN');
    };

    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'A4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const drawHeader = (startY: number) => {
      let currentY = startY;

      if (companyName) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        const companyNameWidth = doc.getTextWidth(companyName);
        doc.text(companyName, (pageWidth - companyNameWidth) / 2, currentY);
        currentY += 10;
      }

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      if (companyAddressLine1) {
        const addr1Width = doc.getTextWidth(companyAddressLine1);
        doc.text(companyAddressLine1, (pageWidth - addr1Width) / 2, currentY);
        currentY += 8;
      }
      if (companyAddressLine2) {
        const addr2Width = doc.getTextWidth(companyAddressLine2);
        doc.text(companyAddressLine2, (pageWidth - addr2Width) / 2, currentY);
        currentY += 8;
      }
      if (companyContactLine) {
        const contactWidth = doc.getTextWidth(companyContactLine);
        doc.text(companyContactLine, (pageWidth - contactWidth) / 2, currentY);
        currentY += 10;
      }

      // First separator line (below company info)
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.5);
      doc.line(40, currentY, pageWidth - 40, currentY);
      currentY += 8;

      // Customer name - centered, bold
      if (customerName) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        const customerNameWidth = doc.getTextWidth(customerName.toUpperCase());
        doc.text(customerName.toUpperCase(), (pageWidth - customerNameWidth) / 2, currentY);
        currentY += 8;
      }

      // Customer number - centered, bold
      if (customerNumber) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        const customerNumberWidth = doc.getTextWidth(customerNumber);
        doc.text(customerNumber, (pageWidth - customerNumberWidth) / 2, currentY);
        currentY += 8;
      }

      // Second separator line (before ledger period)
      doc.line(40, currentY, pageWidth - 40, currentY);
      currentY += 8;

      // Ledger period title - left aligned
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      const ledgerTitle = `Customer ledger : From ${startDateDisplay} to ${endDateDisplay}`;
      doc.text(ledgerTitle, 40, currentY);
      currentY += 10;

      // Final separator line below ledger period
      doc.line(40, currentY, pageWidth - 40, currentY);
      currentY += 6;

      return currentY;
    };

    const tableStartY = drawHeader(25);

    const tableData: any[] = [];

     // Opening Balance row - format: "Opening Balance : Rs.XXXX"
    const openingBalanceFormatted = openingBalance < 0 
      ? `-${formatNumber(Math.abs(openingBalance))}` 
      : formatNumber(openingBalance);
    tableData.push([
      `Opening Balance : Rs.${openingBalanceFormatted}`,
      '',
      '',
      '',
      ''
    ]);

 


    const openingBalanceRowIndex = 0;

    // Process transactions
    const invoiceTransactions: Array<{ date: string; activity: string; reference: string; debit: number; credit: number; products?: string[] }> = [];
    const paymentTransactions: Array<{ date: string; activity: string; reference: string; debit: number; credit: number; invoiceSeqNo?: string }> = [];

    const processedPaymentIds = new Set<string>();

    invoices.forEach((invoiceItem: any) => {
      const invoice = invoiceItem.Invoice;
      if (invoice.InvoiceId) {
        // Only process invoices with InvoiceId - skip standalone payments here
        const invoiceDate = formatDisplayDate(invoice.CreatedOn);
        const invoiceNumber = invoice.InvSeqNo ? `Invoice No ${invoice.InvSeqNo}` : '';

        // Collect products separately
        const products: string[] = [];
        if (invoiceItem.InvoiceLineItems && invoiceItem.InvoiceLineItems.length > 0) {
          invoiceItem.InvoiceLineItems.forEach((item: any) => {
            const qty = item.Quantity || 1;
            const productName = item.ProductName || '';
            products.push(`${productName} (Qty : ${qty})`);
          });
        }

        invoiceTransactions.push({
          date: invoiceDate,
          activity: 'Sales',
          reference: invoice.Reference || invoiceNumber,
          debit: invoice.InvoicedAmount || 0,
          credit: 0,
          products: products
        });

        // Process payments for this invoice
        if (invoiceItem.Payments && invoiceItem.Payments.length > 0) {
          invoiceItem.Payments.forEach((payment: any) => {
            // Create more robust payment ID to avoid duplicates
            const paymentId = payment.ID || payment.PaymentId || 
              `${payment.PaidOn}-${payment.Amount}-${payment.Status}-${payment.Reference || ''}`;
            if (processedPaymentIds.has(paymentId)) return;
            processedPaymentIds.add(paymentId);

            const paymentDate = formatDisplayDate(payment.PaidOn);
            const paymentMethod = payment.PaymentMode || getPaymentMethodFromStatus(payment.Status ?? 0);
            
            // Format receipt reference: "REFERENCE - PaymentMethod" or just "PaymentMethod"
            let paymentReference = '';
            
            if (payment.Reference && payment.Reference.trim() !== '') {
              // Use the reference and append payment method
              paymentReference = `${payment.Reference} - ${paymentMethod}`;
            } else {
              // No reference - show only payment method with dash prefix
              paymentReference = `-${paymentMethod}`;
            }

            paymentTransactions.push({
              date: paymentDate,
              activity: 'Receipt',
              reference: paymentReference,
              debit: 0,
              credit: payment.Amount || 0
            });
          });
        }
      } else if (!invoice.InvoiceId && invoice.Note) {
        // Process standalone payments (no invoice) - these should not have invoice references
        if (invoiceItem.Payments && invoiceItem.Payments.length > 0) {
          invoiceItem.Payments.forEach((payment: any) => {
            // Create more robust payment ID to avoid duplicates
            const paymentId = payment.ID || payment.PaymentId || 
              `${payment.PaidOn}-${payment.Amount}-${payment.Status}-${payment.Reference || ''}`;
            if (processedPaymentIds.has(paymentId)) return;
            processedPaymentIds.add(paymentId);

            const paymentDate = formatDisplayDate(payment.PaidOn);
            const paymentMethod = payment.PaymentMode || getPaymentMethodFromStatus(payment.Status ?? 0);
            
            // For standalone payments, show custom reference if available, otherwise only payment method
            let paymentReference = '';
            if (payment.Reference && payment.Reference.trim() !== '') {
              // Use the reference and append payment method
              paymentReference = `${payment.Reference} - ${paymentMethod}`;
            } else {
              // No reference - show payment method with dash prefix
              paymentReference = `-${paymentMethod}`;
            }

            paymentTransactions.push({
              date: paymentDate,
              activity: 'Receipt',
              reference: paymentReference,
              debit: 0,
              credit: payment.Amount || 0
            });
          });
        }
      }
    });

    // Combine and sort transactions
    const allTransactionsUnsorted = [...invoiceTransactions, ...paymentTransactions];

    const transactionsByDate: Record<string, typeof allTransactionsUnsorted> = {};
    allTransactionsUnsorted.forEach(transaction => {
      if (!transactionsByDate[transaction.date]) {
        transactionsByDate[transaction.date] = [];
      }
      transactionsByDate[transaction.date].push(transaction);
    });

    const extractInvoiceNumber = (reference: string, activity: string): number => {
      if (activity === 'Sales') {
        const match = reference.match(/Invoice No (\d+)/);
        return match ? parseInt(match[1], 10) : 0;
      } else if (activity === 'Receipt') {
        // Extract from format "2800 - Cash", "2800 - Cash 118", or just "Cash" (no invoice number)
        const match = reference.match(/^(\d+)/);
        return match ? parseInt(match[1], 10) : 0;
      }
      return 0;
    };

    // Sort transactions within each date
    Object.keys(transactionsByDate).forEach(date => {
      transactionsByDate[date].sort((a, b) => {
        // Sales come before Receipts
        if (a.activity === 'Sales' && b.activity === 'Receipt') return -1;
        if (a.activity === 'Receipt' && b.activity === 'Sales') return 1;

        if (a.activity === b.activity) {
          // For both Sales and Receipts, sort by invoice number
          const aInvoiceNum = extractInvoiceNumber(a.reference, a.activity);
          const bInvoiceNum = extractInvoiceNumber(b.reference, b.activity);
          
          // For receipts without invoice numbers, put them at the end
          if (aInvoiceNum === 0 && bInvoiceNum > 0) return 1;
          if (aInvoiceNum > 0 && bInvoiceNum === 0) return -1;
          
          // Sort by invoice number
          const invoiceComparison = aInvoiceNum - bInvoiceNum;
          if (invoiceComparison !== 0) return invoiceComparison;
          
          // If same invoice number, sort by payment method (for receipts)
          if (a.activity === 'Receipt') {
            // Extract payment method from reference (remove invoice number and dash if present)
            const aMethod = a.reference.replace(/^\d+\s*-\s*/, '').trim();
            const bMethod = b.reference.replace(/^\d+\s*-\s*/, '').trim();
            return aMethod.localeCompare(bMethod);
          }
        }
        return 0;
      });
    });

    // Sort dates chronologically
    const sortedDates = Object.keys(transactionsByDate).sort((a, b) => {
      const dateA = new Date(a.split('-').reverse().join('-'));
      const dateB = new Date(b.split('-').reverse().join('-'));
      return dateA.getTime() - dateB.getTime();
    });

    const allTransactions = sortedDates.flatMap(date => transactionsByDate[date]);

    // Build table data with product rows
    allTransactions.forEach(transaction => {
      if (transaction.activity === 'Sales') {
        // Add main invoice row
        tableData.push([
          transaction.date,
          transaction.activity,
          transaction.reference,
          transaction.debit > 0 ? formatNumber(transaction.debit) : '',
          transaction.credit > 0 ? formatNumber(transaction.credit) : ''
        ]);

        // Add product rows if any - check if products property exists
        const salesTransaction = transaction as { products?: string[] };
        if (salesTransaction.products && salesTransaction.products.length > 0) {
          salesTransaction.products.forEach(product => {
            tableData.push([
              '',
              '',
              product.trim(),
              '',
              ''
            ]);
          });
        }
      } else {
        // Receipt row
        tableData.push([
          transaction.date,
          transaction.activity,
          transaction.reference,
          transaction.debit > 0 ? formatNumber(transaction.debit) : '',
          transaction.credit > 0 ? formatNumber(transaction.credit) : ''
        ]);
      }
    });

    // Calculate totals row index
    const hasTotalsRow = allTransactions.length > 0;
    let totalsRowIndex = -1;

    if (hasTotalsRow) {
      const totalDebit = allTransactions.reduce((sum, t) => sum + t.debit, 0);
      const totalCredit = allTransactions.reduce((sum, t) => sum + t.credit, 0);

      // Add a separator line before total debit/credit
      tableData.push([
        { 
          content: '', 
          colSpan: 5, 
          styles: { 
            lineWidth: { top: 0.3, left: 0, right: 0, bottom: 0 },
            lineColor: { top: [0, 0, 0], left: [255, 255, 255], right: [255, 255, 255], bottom: [255, 255, 255] },
            cellPadding: 0,
            minCellHeight: 2
          } 
        }
      ]);

      totalsRowIndex = tableData.length;

      tableData.push([
        '',
        '',
        '',
        formatNumber(totalDebit),
        formatNumber(totalCredit)
      ]);
    }

    const closingBalanceRowIndex = tableData.length;

    // Add a separator line before closing balance
    tableData.push([
      { 
        content: '', 
        colSpan: 5, 
        styles: { 
          lineWidth: { top: 0.3, left: 0, right: 0, bottom: 0 },
          lineColor: { top: [0, 0, 0], left: [255, 255, 255], right: [255, 255, 255], bottom: [255, 255, 255] },
          cellPadding: 0,
          minCellHeight: 2
        } 
      }
    ]);

    // Closing Balance row - format: "Closing Balance : Rs.XXXX"
    const closingBalanceFormatted = closingBalance < 0 
      ? `-${formatNumber(Math.abs(closingBalance))}` 
      : formatNumber(closingBalance);
    tableData.push([
      `Closing Balance : Rs.${closingBalanceFormatted}`,
      '',
      '',
      '',
      ''
    ]);

    // Create table with proper formatting
    autoTable(doc, {
      startY: tableStartY + 5,
      head: [['Date', 'Activity', 'Reference', 'Debit (Rs.)', 'Credit (Rs.)']],
      body: tableData,
      theme: 'plain',
      styles: {
        fontSize: 9,
        halign: 'center',
        valign: 'middle',
        textColor: [0, 0, 0],
        lineColor: [255, 255, 255], // Hide all default borders
        lineWidth: 0,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 9,
        halign: 'center',
        lineColor: [255, 255, 255], // Hide default borders, we'll draw manually
        lineWidth: 0,
        cellPadding: 3,
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontSize: 8,
        halign: 'center',
        lineColor: [255, 255, 255], // Hide all body borders
        lineWidth: 0,
        cellPadding: 3,
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 120, lineColor: [255, 255, 255] }, // Date - increased width for Opening Balance text
        1: { halign: 'center', cellWidth: 60, lineColor: [255, 255, 255] }, // Activity
        2: { halign: 'left', lineColor: [255, 255, 255] }, // Reference - keep left-aligned
        3: { halign: 'center', cellWidth: 70, lineColor: [255, 255, 255] }, // Debit
        4: { halign: 'center', cellWidth: 70, lineColor: [255, 255, 255] }, // Credit
      },
      margin: { left: 40, right: 40, top: 120 },
      tableLineColor: [255, 255, 255], // Hide table borders
      tableLineWidth: 0,
      showHead: 'everyPage',
      didParseCell: (data: any) => {
        if (data.section === 'head') {
          // Header row - keep Reference column left-aligned
          if (data.column.index === 2) {
            data.cell.styles.halign = 'left';
          }
        } else if (data.section === 'body') {
          const rowData = data.row.raw;
          const isProductRow = rowData[0] === '' && rowData[1] === '' && rowData[2] !== '' && !rowData[0].toString().startsWith('Opening') && !rowData[0].toString().startsWith('Closing');
          const isOpeningBalanceRow = rowData[0].toString().startsWith('Opening Balance');
          const isClosingBalanceRow = rowData[0].toString().startsWith('Closing Balance');

          if (isProductRow) {
            // Product rows - right align reference column, normal font
            if (data.column.index === 2) {
              data.cell.styles.halign = 'right';
              data.cell.styles.fontStyle = 'normal';
              data.cell.styles.fontSize = 7;
            }
          } else if (isOpeningBalanceRow || isClosingBalanceRow) {
            // Balance rows - left align first column (text), center Debit/Credit
            if (data.column.index === 0) {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.halign = 'left';
            } else if (data.column.index === 3 || data.column.index === 4) {
              // Debit/Credit columns for balance rows - center aligned
              data.cell.styles.fontStyle = 'normal';
              data.cell.styles.halign = 'center';
            }

            if (isClosingBalanceRow) {
              data.cell.styles.lineColor = [0, 0, 0];
              data.cell.styles.lineWidth = { top: 0, left: 0, right: 0, bottom: 0.3 };
            }
          } else if (hasTotalsRow && data.row.index === totalsRowIndex) {
            // Totals row - bold, center aligned for Debit/Credit
            if (data.column.index === 3 || data.column.index === 4) {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.halign = 'center';
            }
          } else {
            // Regular transaction rows - center aligned (except Reference which stays left)
            data.cell.styles.fontStyle = 'normal';
            if (data.column.index !== 2) {
              data.cell.styles.halign = 'center';
            }
          }
        }
      },
      didDrawCell: (data: any) => {
        const { doc, cell, row, column, section } = data;
        const startX = data.settings.margin.left || 40;
        const endX = doc.internal.pageSize.getWidth() - (data.settings.margin.right || 40);

        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(1); // Increased line width for better visibility

        // Debug logging for all cells
        if (section === 'body') {
          console.log('Cell data:', {
            section,
            row: row.index,
            column: column.index,
            raw: row.raw,
            cellY: cell.y,
            cellHeight: cell.height
          });
        }

        // Draw header bottom border (on last column of any header row)
        if (section === 'head' && column.index === 4) {
          console.log('Drawing header bottom border');
          const y = cell.y + cell.height;
          if (y > 0 && y < doc.internal.pageSize.getHeight() && startX < endX) {
            doc.line(startX, y, endX, y);
          }
        }

        // Draw line before opening balance (on first column of opening balance row)
        if (section === 'body' && column.index === 0 && row.raw) {
          const rowData = row.raw;
          const firstCell = rowData[0];
          console.log('Checking opening balance:', firstCell);
          if (firstCell && typeof firstCell === 'string' && firstCell.includes('Opening Balance')) {
            console.log('Drawing line before opening balance:', firstCell);
            const y = cell.y;
            const tableStartX = data.table.x;
            const tableEndX = data.table.x + data.table.width;
            if (y > 0 && y < doc.internal.pageSize.getHeight() && tableStartX < tableEndX) {
              doc.line(tableStartX, y, tableEndX, y);
            }
          }
        }

        // Draw line before totals (on first column of totals row)
        if (false && section === 'body' && column.index === 0 && row.raw) {
          const rowData = row.raw;
          const isFirstCellEmpty = rowData[0] === '' && rowData[1] === '' && rowData[2] === '';
          const hasDebitOrCredit = rowData[3] !== '' && rowData[4] !== '';
          console.log('Checking totals row:', { isFirstCellEmpty, hasDebitOrCredit, rowData, hasTotalsRow });
          // Check if this looks like a totals row and if we have transactions
          if (isFirstCellEmpty && hasDebitOrCredit && hasTotalsRow) {
            console.log('Drawing line before totals row:', rowData);
            const y = cell.y;
            const tableStartX = data.table.x;
            const tableEndX = data.table.x + data.table.width;
            if (y > 0 && y < doc.internal.pageSize.getHeight() && tableStartX < tableEndX) {
              doc.line(tableStartX, y, tableEndX, y);
            }
          }
        }

        // Draw line before closing balance (on first column of closing balance row)
        if (false && section === 'body' && column.index === 0 && row.raw) {
          const rowData = row.raw;
          const firstCell = rowData[0];
          console.log('Checking closing balance:', firstCell);
          if (firstCell && typeof firstCell === 'string' && firstCell.includes('Closing Balance')) {
            console.log('Drawing line before closing balance:', firstCell);
            const y = cell.y;
            const tableStartX = data.table.x;
            const tableEndX = data.table.x + data.table.width;
            if (y > 0 && y < doc.internal.pageSize.getHeight() && tableStartX < tableEndX) {
              doc.line(tableStartX, y, tableEndX, y);
            }
          }
        }

        // Draw line after closing balance (on last column of closing balance row)
        if (false && section === 'body' && column.index === 4 && row.raw) {
          const rowData = row.raw;
          const firstCell = rowData[0];
          console.log('Checking closing balance after:', firstCell);
          if (firstCell && typeof firstCell === 'string' && firstCell.includes('Closing Balance')) {
            console.log('Drawing line after closing balance:', firstCell);
            const y = cell.y + cell.height;
            const tableStartX = data.table.x;
            const tableEndX = data.table.x + data.table.width;
            if (y > 0 && y < doc.internal.pageSize.getHeight() && tableStartX < tableEndX) {
              doc.line(tableStartX, y, tableEndX, y);
            }
          }
        }
      },
      didDrawPage: (data: any) => {
        // Draw header on every page after the first
        if (data.pageNumber > 1) {
          drawHeader(25);
        }
      },
    });

    // Draw horizontal lines after table is created
    if (false) {
      const finalY = (doc as any).lastAutoTable.finalY || tableStartY;
      const tableWidth = (doc as any).lastAutoTable.width || (pageWidth - 80);
      const tableStartX = 40;
      const tableEndX = tableStartX + tableWidth;

      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(1);

      // Find and draw line before totals row
      if (hasTotalsRow && totalsRowIndex >= 0) {
        const totalsRowY = tableStartY + 5 + (totalsRowIndex + 1) * 15; // Approximate row height
        console.log('Drawing totals line at Y:', totalsRowY);
        doc.line(tableStartX, totalsRowY, tableEndX, totalsRowY);
      }

      // Find and draw line before closing balance
      const closingBalanceRowY = tableStartY + 5 + (closingBalanceRowIndex + 1) * 15;
      console.log('Drawing closing balance line at Y:', closingBalanceRowY);
      doc.line(tableStartX, closingBalanceRowY, tableEndX, closingBalanceRowY);

      // Draw line after closing balance
      const afterClosingBalanceY = closingBalanceRowY + 15;
      console.log('Drawing after closing balance line at Y:', afterClosingBalanceY);
      doc.line(tableStartX, afterClosingBalanceY, tableEndX, afterClosingBalanceY);
      void finalY;
    }

    // Add page numbers to all pages after the table is fully generated
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`Page ${i} of ${pageCount}`, 40, pageHeight - 15);
    }

    const pdfDataUri = doc.output('datauristring');
    downloadPdf(
      pdfDataUri,
      `DetailedLedger_${customerName}_${startDateDisplay}_${endDateDisplay}`,
      () => {
        toast({
          title: "Export Success",
          description: Capacitor.isNativePlatform()
            ? "PDF saved to device"
            : "Detailed customer ledger PDF exported successfully",
          className: 'bg-green-600 text-white border-green-700'
        });
      },
      (error) => {
        toast({
          title: "Export Error",
          description: error,
          variant: "destructive"
        });
      }
    );
  } catch (error) {
    console.error('Detailed Ledger PDF export error:', error);
    toast({
      title: "Export Error",
      description: error instanceof Error ? error.message : "Failed to export detailed customer ledger",
      variant: "destructive"
    });
  }
}, [selectedCustomers, customers, dateRange, token, toast, getPaymentMethodFromStatus, company]);

  const handleExportToExcel = useCallback(async () => {
    try {
      // Check if any customers are selected
      if (selectedCustomers.length === 0) {
        toast({
          title: "No Selection",
          description: "Please select at least one customer to export to Excel",
          variant: "destructive"
        });
        return;
      }

      toast({
        title: "Generating Excel...",
        description: "Preparing customer dues data for export",
      });

      // Use the same data shown in the UI
      const selectedRows = customers.filter((c) => selectedCustomers.includes(c.id));

      const titleRow = ['All Customer Dues'];
      const headerRow = [
        'Name',
        'Buyer',
        'Phone',
        'Opening Balance',
        'Total Invoiced Amount',
        'Total Paid Amount',
        'Total Due Amount',
        'Due Since Days',
      ];

      const dataRows = selectedRows.map((c) => [
        c.name || '',
        c.buyer || '',
        c.phone || '',
        Number(c.openingBalance) || 0,
        Number(c.totalInvoiced) || 0,
        Number(c.totalPaid) || 0,
        Number(c.totalDue) || 0,
        Number(c.dueSinceDays) || 0,
      ]);

      const ws = XLSX.utils.aoa_to_sheet([titleRow, headerRow, ...dataRows]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'All Customer Dues');

      // Set row height for title row to make it more prominent
      ws['!rows'] = [
        { hpt: 30 }, // Title row height
        { hpt: 20 }, // Header row height
      ];

      // Merge title across all columns (A1 to H1)
      ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 7 } }];

      // Explicitly set title cell with styling
      ws['A1'] = { 
        v: 'All Customer Dues', 
        t: 's',
        s: {
          font: { sz: 18, bold: true, color: { rgb: 'FFFF0000' } }, // Red color, size 18, bold
          alignment: { horizontal: 'center', vertical: 'center' },
          fill: { fgColor: { rgb: 'FFFFFFFF' } } // White background
        }
      };

      // Apply styling to header row (bold, centered)
      const headerCells = ['A2', 'B2', 'C2', 'D2', 'E2', 'F2', 'G2', 'H2'];
      headerCells.forEach((cell, index) => {
        ws[cell] = { 
          v: headerRow[index], 
          t: 's',
          s: {
            font: { bold: true, sz: 12 },
            alignment: { horizontal: 'center', vertical: 'center' },
            fill: { fgColor: { rgb: 'F0F0F0' } } // Light gray background for headers
          }
        };
      });

      // Set column widths for better readability
      const colWidths = [
        { wch: 25 }, // Name
        { wch: 20 }, // Buyer
        { wch: 15 }, // Phone
        { wch: 15 }, // Opening Balance
        { wch: 18 }, // Total Invoiced Amount
        { wch: 16 }, // Total Paid Amount
        { wch: 15 }, // Total Due Amount
        { wch: 12 }  // Due Since Days
      ];
      ws['!cols'] = colWidths;

      // Generate Excel file with styling
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array', cellStyles: true });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'All_Customer_Dues.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: "Export Success",
        description: "Customer dues exported to Excel successfully",
        className: 'bg-green-600 text-white border-green-700'
      });
    } catch (error) {
      console.error('Excel export error:', error);
      toast({
        title: "Export Error",
        description: error instanceof Error ? error.message : "Failed to export customer dues to Excel",
        variant: "destructive"
      });
    }
  }, [selectedCustomers, customers, toast]);

  const handleCreateLedgerPDF = useCallback(async () => {
    if (selectedCustomers.length === 0) {
      toast({
        title: "No Customers Selected",
        description: "Please select at least one customer to generate the ledger",
        variant: "destructive"
      });
      return;
    }

    if (!dateRange.start || !dateRange.end) {
      toast({
        title: "Date Range Required",
        description: "Please select start and end dates for the ledger",
        variant: "destructive"
      });
      return;
    }

    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'A4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      const baseUrl = getBaseUrl();
      const rawToken = token || localStorage.getItem('token') || '';
      const authToken = rawToken ? (rawToken.startsWith('Bearer ') ? rawToken : `Bearer ${rawToken}`) : '';

      // Format dates for display (DD-MM-YYYY)
      const formatDateDisplay = (dateStr: string) => {
        const [year, month, day] = dateStr.split('-');
        return `${day}-${month}-${year}`;
      };

      // Format dates for API (DD-MM-YYYY)
      const formatDateForAPI = (dateStr: string) => {
        const [year, month, day] = dateStr.split('-');
        return `${day}-${month}-${year}`;
      };

      const startDateDisplay = formatDateDisplay(dateRange.start);
      const endDateDisplay = formatDateDisplay(dateRange.end);

      let isFirstCustomer = true;

      // Fetch and generate ledger for each selected customer
      for (const customerId of selectedCustomers) {
        const customer = customers.find(c => c.id === customerId);
        if (!customer) continue;

        const customerCode = customer.customerCode || customer.id;

        // Add new page for each customer (except the first)
        if (!isFirstCustomer) {
          doc.addPage();
        }
        isFirstCustomer = false;

        // First, fetch customer data from CustomerList API to get Opening Balance
        // Use filter=all to get all customer data, then find the specific customer
        const customerListEndpoint = `${baseUrl}/api/CustomerList?columnname=fname&direction=DESC&offset=0&limit=1&searchstr=&cgroupid=0&filter=all`;
        
        const customerListResponse = await fetch(customerListEndpoint, {
          headers: {
            'Authorization': authToken,
            'Content-Type': 'application/json',
          },
        });

        let customerData: any = {};
        if (customerListResponse.ok) {
          const customerListResult = await customerListResponse.json();
          console.log('🛰️ CustomerList response:', JSON.stringify(customerListResult, null, 2));
          if (customerListResult.data && customerListResult.data.length > 0) {
            // Find the customer by CustomerCode
            customerData = customerListResult.data.find((c: any) => 
              String(c.Customercode) === String(customerCode)
            );
            if (!customerData) {
              // Fallback to first customer if not found
              customerData = customerListResult.data[0];
            }
            console.log('🛰️ Selected customer data:', JSON.stringify(customerData, null, 2));
          }
        }

        // Then fetch customer ledger data with invoices and payments
        const endpoint = `${baseUrl}/api/CustInvoicesPayments?CustomerCode=${customerCode}&StartDate=${formatDateForAPI(dateRange.start)}&EndDate=${formatDateForAPI(dateRange.end)}`;
        
        const response = await fetch(endpoint, {
          headers: {
            'Authorization': authToken,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch data for customer ${customer.name}`);
        }

        const data = await response.json();
        
        console.log('🛰️ CustInvoicesPayments response for Create Ledger:', JSON.stringify(data, null, 2));
        
        // Use Opening Balance from CustomerList API, fallback to CustInvoicesPayments API
        const openingBalance = customerData.OpeningBalance || data.OpeningBalance || data.data?.OpeningBalance || 0;
        const closingBalance = data.ClosingBalance || data.data?.ClosingBalance || 0;
        const invoices = data.Invoices || data.data?.Invoices || [];
        
        // Get additional data from CustomerList API
        const totalInvoicedAmount = customerData['Total Invoiced Amount'] || data.TotalInvoicedAmount || data.data?.TotalInvoicedAmount || 0;
        const totalPaidAmount = customerData['Total Paid Amount'] || data.TotalPaidAmount || data.data?.TotalPaidAmount || 0;
        const totalDueAmount = customerData['Total Due Amount'] || data.TotalDueAmount || data.data?.TotalDueAmount || 0;

        // Calculate sum of invoiced amounts from invoices data
        const sumInvoicedAmount = invoices.reduce((sum: number, invoiceItem: any) => {
          const invoice = invoiceItem.Invoice;
          const amount = invoice?.InvoicedAmount ?? invoice?.InvoiceAmount ?? 0;
          return sum + Number(amount);
        }, 0);

        // Calculate sum of paid amounts from payments data
        const sumPaidAmount = invoices.reduce((sum: number, invoiceItem: any) => {
          const payments = invoiceItem.Payments || [];
          const paymentSum = payments.reduce((paymentTotal: number, payment: any) => {
            const amount = payment?.PaidAmount ?? payment?.Amount ?? 0;
            return paymentTotal + Number(amount);
          }, 0);
          return sum + paymentSum;
        }, 0);

        // Draw header
        const drawHeader = (startY: number) => {
          let currentY = startY;

          // Logo box placeholder (top left) - 10x10 pts
          doc.setDrawColor(0, 0, 0);
          doc.setLineWidth(1);
          doc.rect(40, currentY, 10, 10); // Small box for logo (10x10)

          currentY += 20;

          // Ledger Period (centered, bold)
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          const ledgerTitle = `Ledger of ${customer.name || 'Customer'}`;
          const titleWidth = doc.getTextWidth(ledgerTitle);
          doc.text(ledgerTitle, (pageWidth - titleWidth) / 2, currentY);
          currentY += 15;

          // Date Range (centered)
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          const dateRangeText = `Between ${startDateDisplay} and ${endDateDisplay}`;
          const dateWidth = doc.getTextWidth(dateRangeText);
          doc.text(dateRangeText, (pageWidth - dateWidth) / 2, currentY);
          currentY += 15;

          return currentY;
        };

        // Draw header and get starting Y position
        const tableStartY = drawHeader(10);

        // Summary table data
        const summaryData = [
          ['Opening Balance', 'Total Invoiced Amount', 'Total Paid Amount', 'Total Due Amount', 'Total Invoices', 'Invoiced Between Dates', 'Paid During Dates'],
          [
            openingBalance.toFixed(2),
            totalInvoicedAmount.toFixed(2),
            totalPaidAmount.toFixed(2),
            totalDueAmount.toFixed(2),
            String(customerData['Total Invoices Made'] || invoices.length || 0),
            sumInvoicedAmount.toFixed(2),
            sumPaidAmount.toFixed(2)
          ]
        ];

        // Add summary table
        autoTable(doc, {
          startY: tableStartY,
          head: [summaryData[0]],
          body: [summaryData[1]],
          theme: 'grid',
          styles: {
    fontSize: 8,
    cellPadding: 1.5,                       // ← Very tight padding (was probably ~4-5 default)
    valign: 'middle',
    overflow: 'linebreak',
    minCellHeight: 12,                      // ← Force shorter rows (default ~18-20)
  },
  headStyles: {
    fillColor: [255, 255, 255],
    textColor: [0, 0, 0],
    fontStyle: 'bold',
    fontSize: 8.0,                          // Slightly bigger for header visibility
    cellPadding: 2,                         // Header a bit taller than body
    lineColor: [0, 0, 0],                   // Black border for header
    lineWidth: 0.3,
  },
  bodyStyles: {
    fontSize: 7.5,                          // Slightly smaller body text
    cellPadding: 1.2,                       // Even tighter for transaction rows
    minCellHeight: 11,                      // Very compact rows
  },
  margin: { left: 40, right: 40, top: 0 }, // ← top: 0 → no extra margin at top of table
  showHead: 'everyPage',
        });

        const invoiceTableStartY = (doc as any).lastAutoTable.finalY + 20;

        // Invoice list header (centered)
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        const invoiceHeaderText = 'List of all Invoices';
        const invoiceHeaderWidth = doc.getTextWidth(invoiceHeaderText);
        doc.text(invoiceHeaderText, (pageWidth - invoiceHeaderWidth) / 2, invoiceTableStartY);

        // Prepare invoice table data - ONLY invoices, not payments
        const invoiceTableData: any[] = [];
        
        // Sort invoices by date in descending order (newest first)
        const sortedInvoices = [...invoices].sort((a: any, b: any) => {
          const dateA = new Date(a.Invoice?.CreatedOn || a.Invoice?.InvoiceDate || 0);
          const dateB = new Date(b.Invoice?.CreatedOn || b.Invoice?.InvoiceDate || 0);
          return dateB.getTime() - dateA.getTime(); // Descending order
        });
        
        sortedInvoices.forEach((invoiceItem: any) => {
          const invoice = invoiceItem.Invoice;

          // Add invoice row ONLY - using correct field names from API
          invoiceTableData.push([
            formatDateDisplay(invoice.CreatedOn?.split('T')[0] || invoice.InvoiceDate?.split('T')[0] || ''),
            (invoice.InvoicedAmount ?? invoice.InvoiceAmount ?? 0).toFixed(2),
            invoice.CompanyName || invoice.CustomerName || '',
            invoice.InvSeqNo || invoice.InvoiceNumber || invoice.InvoiceId || '',
            invoice.LocationName || invoice.Location || ''
          ]);
        });

        // Add invoice table
        autoTable(doc, {
          startY: invoiceTableStartY + 15,
          head: [['Invoice Date', 'Invoiced Amount', 'Company Name', 'Invoice Number', 'Location']],
          body: invoiceTableData,
          theme: 'grid',
          styles: {
            fontSize: 7,
            halign: 'center',
            valign: 'middle',
            textColor: [0, 0, 0],
            lineColor: [0, 0, 0],
            lineWidth: 0.5,
          },
          headStyles: {
            fillColor: [255, 255, 255],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            fontSize: 7,
          },
          bodyStyles: {
            fillColor: [255, 255, 255],
            fontSize: 6,
          },
          margin: { left: 40, right: 40, top: 90, bottom: 40 },
          showHead: 'everyPage',
          didDrawPage: (data: any) => {
            // Draw header on new pages
            if (data.pageNumber > 1) {
              drawHeader(30);
            }

            // Add page number at bottom left
            const pageCount = doc.getNumberOfPages();
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.text(`Page ${data.pageNumber} of ${pageCount}`, 40, pageHeight - 20);
          },
        });

        // Add "Payments Received" section if there are payments
        const allPayments: any[] = [];
        
        invoices.forEach((invoiceItem: any) => {
          const payments = invoiceItem.Payments || [];
          allPayments.push(...payments);
        });

        if (allPayments.length > 0) {
          const paymentTableStartY = (doc as any).lastAutoTable.finalY + 20;

          // Payment header (centered)
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          const paymentHeaderText = 'Payments Received';
          const paymentHeaderWidth = doc.getTextWidth(paymentHeaderText);
          doc.text(paymentHeaderText, (pageWidth - paymentHeaderWidth) / 2, paymentTableStartY);

          // Helper function to get payment method name
          const getPaymentMethodName = (status: number): string => {
            switch (status) {
              case 1: return 'Cash';
              case 2: return 'Card';
              case 3: return 'Wallet';
              case 4: return 'Coupon';
              case 5: return 'Credit Note';
              case 6: return 'Cheque';
              case 7: return 'Bank Transfer';
              default: return 'Cash';
            }
          };

          // Sort payments by date in descending order (newest first)
          const sortedPayments = [...allPayments].sort((a: any, b: any) => {
            const dateA = new Date(a.PaidOn || 0);
            const dateB = new Date(b.PaidOn || 0);
            return dateB.getTime() - dateA.getTime(); // Descending order
          });

          // Prepare payment table data
          const paymentTableData = sortedPayments.map((payment: any) => {
            // Directly use InvSeqNo from payment data if available
            const paymentInvSeqNo = String(payment.InvSeqNo ?? '');
            
            return [
              formatDateDisplay(payment.PaidOn?.split('T')[0] || ''),
              payment.PaidAmount?.toFixed(2) || payment.Amount?.toFixed(2) || '0.00',
              payment.ReceiptNo || payment.Reference || '',
              getPaymentMethodName(payment.Status ?? 0),
              paymentInvSeqNo !== 'NA' && paymentInvSeqNo !== '' ? paymentInvSeqNo : ''
            ];
          });

          // Add payment table
          autoTable(doc, {
            startY: paymentTableStartY + 15,
            head: [['Paid On', 'Paid Amount', 'Receipt No.', 'Type', 'Invoice No']],
            body: paymentTableData,
            theme: 'grid',
            styles: {
              fontSize: 7,
              halign: 'center',
              valign: 'middle',
              textColor: [0, 0, 0],
              lineColor: [0, 0, 0],
              lineWidth: 0.5,
            },
            headStyles: {
              fillColor: [255, 255, 255],
              textColor: [0, 0, 0],
              fontStyle: 'bold',
              fontSize: 7,
            },
            bodyStyles: {
              fillColor: [255, 255, 255],
              fontSize: 6,
            },
            margin: { left: 40, right: 40, top: 90, bottom: 40 },
            didDrawPage: (data: any) => {
              // Draw header on new pages
              if (data.pageNumber > 1) {
                drawHeader(30);
              }

              // Add page number at bottom left
              const pageCount = doc.getNumberOfPages();
              doc.setFontSize(8);
              doc.setFont('helvetica', 'normal');
              doc.text(`Page ${data.pageNumber} of ${pageCount}`, 40, pageHeight - 20);
            },
          });
        }
      }

      // Update page numbers on all pages
      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        
        // Clear the area where page numbers are drawn with a white rectangle
        doc.setFillColor(255, 255, 255);
        doc.rect(40, pageHeight - 25, 100, 10, 'F'); // Fill white rectangle
        
        // Draw final page numbers
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        doc.text(`Page ${i} of ${totalPages}`, 40, pageHeight - 20);
      }

      // Download PDF
      const pdfDataUri = doc.output('datauristring');
      downloadPdf(
        pdfDataUri,
        `CustomerLedger_${startDateDisplay}_${endDateDisplay}`,
        () => {
          toast({
            title: "Export Success",
            description: Capacitor.isNativePlatform()
              ? "PDF saved to device"
              : "Customer ledger PDF exported successfully",
            className: 'bg-green-600 text-white border-green-700'
          });
        },
        (error) => {
          toast({
            title: "Export Error",
            description: error,
            variant: "destructive"
          });
        }
      );
    } catch (error) {
      console.error('Ledger PDF generation error:', error);
      toast({
        title: "Export Error",
        description: error instanceof Error ? error.message : "Failed to generate customer ledger",
        variant: "destructive"
      });
    }
  }, [selectedCustomers, customers, dateRange, token, toast]);

  const fetchCustomerDueInvoices = useCallback(
    async (customerCode: string) => {
      if (!customerCode || invoiceLoading) return;

      const baseUrl = getBaseUrl();
      const rawToken = token || localStorage.getItem('token') || '';
      const authToken = rawToken
        ? rawToken.startsWith('Bearer ')
          ? rawToken
          : `Bearer ${rawToken}`
        : '';
      const endpoint = `${baseUrl}/api/CustomerDueInvoiceNumbers?CustomerCode=${encodeURIComponent(
        customerCode,
      )}`;

      console.log('🛰️ CustomerDueInvoiceNumbers request:', endpoint);
      setInvoiceLoading(true);
      setInvoiceOptions([]);

      try {
        const response = await fetch(endpoint, {
          headers: {
            ...(authToken ? { Authorization: authToken } : {}),
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          console.error(
            'CustomerDueInvoiceNumbers error status:',
            response.status,
            response.statusText,
          );
          throw new Error('Failed to fetch customer invoices');
        }

        const json = await response.json();
        console.log('🛰️ CustomerDueInvoiceNumbers response:', json);

        if (json.status === 0 && Array.isArray(json.data)) {
          const opts: Array<{ invSeqNo: string; invoiceId: string; companyId?: number }> =
            json.data
              .map((item: any) => {
                const invSeqNo = item.InvSeqNo != null ? String(item.InvSeqNo) : '';
                const invoiceId =
                  item.Invoiceid != null
                    ? String(item.Invoiceid)
                    : item.InvoiceID != null
                    ? String(item.InvoiceID)
                    : '';
                const companyId =
                  item.CompanyID != null ? Number(item.CompanyID) : undefined;
                return invSeqNo && invoiceId
                  ? {
                      invSeqNo,
                      invoiceId,
                      companyId,
                    }
                  : null;
              })
              .filter(Boolean) as Array<{
              invSeqNo: string;
              invoiceId: string;
              companyId?: number;
            }>;

          setInvoiceOptions(opts);
        } else {
          toast({
            variant: 'destructive',
            description: json.message || 'Unable to load invoices for this customer',
          });
        }
      } catch (error) {
        console.error('CustomerDueInvoiceNumbers fetch failed:', error);
        toast({
          variant: 'destructive',
          description: 'Unable to load invoices for this customer',
        });
        setInvoiceOptions([]);
      } finally {
        setInvoiceLoading(false);
      }
    },
    [invoiceLoading, token, toast],
  );

  // Function to refresh customer ledger data after payment
  const refreshCustomerLedgerData = async (customerCode?: string, startDate?: string, endDate?: string) => {
    console.log('🔄 Refreshing customer ledger data after payment...');
    
    const baseUrl = getBaseUrl();
    const rawToken = token || localStorage.getItem('token') || '';
    const authToken = rawToken ? (rawToken.startsWith('Bearer ') ? rawToken : `Bearer ${rawToken}`) : '';
    
    // Format dates for API (DD-MM-YYYY)
    const formatDateForAPI = (dateStr: string) => {
      const [year, month, day] = dateStr.split('-');
      return `${day}-${month}-${year}`;
    };
    
    try {
      // Build query parameters for the API call
      const params = new URLSearchParams();
      
      // Use passed parameters or fall back to current state
      if (customerCode) {
        params.set('CustomerCode', customerCode);
      } else if (selectedCustomers.length > 0) {
        // If specific customers are selected, refresh data for those customers
        for (const customerId of selectedCustomers) {
          const customer = customers.find(c => c.id === customerId);
          if (customer?.customerCode) {
            params.set('CustomerCode', customer.customerCode);
            break; // Use first selected customer's code
          }
        }
      }
      
      // Use passed dates or fall back to current state
      const start = startDate || (dateFilterEnabled && dateRange.start ? dateRange.start : getTodayStr());
      const end = endDate || (dateFilterEnabled && dateRange.end ? dateRange.end : getTodayStr());
      
      params.set('StartDate', formatDateForAPI(start));
      params.set('EndDate', formatDateForAPI(end));
      
      if (debouncedSearchTerm) {
        params.set('SearchTerm', debouncedSearchTerm);
      }
      
      const endpoint = `${baseUrl}/api/CustInvoicesPayments?${params.toString()}`;
      console.log('🛰️ Refreshing customer ledger data:', endpoint);
      
      const response = await fetch(endpoint, {
        headers: {
          ...(authToken ? { Authorization: authToken } : {}),
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const json = await response.json();
        console.log('🛰️ Customer ledger data refreshed successfully:', json);
        
        // Update the customer data with the refreshed information
        if (json && customerCode) {
          // Find the customer in the current state and update their data
          setCustomers(prevCustomers => 
            prevCustomers.map(customer => {
              if (customer.customerCode === customerCode) {
                // Update customer with fresh data from API
                return {
                  ...customer,
                  totalInvoiced: json.TotalInvoicedAmount || customer.totalInvoiced,
                  totalPaid: json.TotalPaidAmount || customer.totalPaid,
                  totalDue: json.TotalDueAmount || customer.totalDue,
                  dueSinceDays: json.NumberOfDaysTillToday || customer.dueSinceDays,
                  openingBalance: json.OpeningBalance || customer.openingBalance,
                };
              }
              return customer;
            })
          );
        }
        
        // Also refresh the invoices for this customer
        if (customerCode) {
          const customerId = customers.find(c => c.customerCode === customerCode)?.id;
          if (customerId) {
            fetchCustomerInvoices(customerId, customerCode);
          }
        }
        
        toast({ description: 'Data refreshed successfully' });
      } else {
        console.error('Failed to refresh customer ledger data:', response.status);
        toast({ variant: 'destructive', description: 'Failed to refresh data' });
      }
    } catch (error) {
      console.error('Error refreshing customer ledger data:', error);
      toast({ variant: 'destructive', description: 'Error refreshing data' });
    }
  };

  const handlePaymentSubmit = async () => {
    if (!paymentForm.customerId) {
      toast({ variant: 'destructive', description: 'Customer missing' });
      return;
    }
    const received = Number(paymentForm.paymentReceived);
    if (!Number.isFinite(received)) {
      toast({ variant: 'destructive', description: 'Payment must be a valid number' });
      return;
    }
    if (!paymentForm.companySelected) {
      toast({ variant: 'destructive', description: 'Select a company' });
      return;
    }
    if (paymentForm.linkInvoice) {
      if (!paymentForm.invoiceSelected || !paymentForm.selectedInvoiceId) {
        toast({ variant: 'destructive', description: 'Select an invoice' });
        return;
      }
    }
    if (
      paymentForm.paymentMethod === 'Bank Transfer' &&
      (!paymentForm.bankName || !paymentForm.accountNumber)
    ) {
      toast({ variant: 'destructive', description: 'Select bank and account for bank transfer' });
      return;
    }

    // Resolve CompanyID
    let companyIdStr: string | undefined;
    if (paymentForm.linkInvoice && paymentForm.selectedInvoiceCompanyId != null) {
      companyIdStr = String(paymentForm.selectedInvoiceCompanyId);
    } else if (paymentForm.companySelected && companyMap[paymentForm.companySelected]) {
      companyIdStr = companyMap[paymentForm.companySelected];
    }

    if (!companyIdStr) {
      toast({
        variant: 'destructive',
        description: 'Unable to resolve CompanyID. Please select a valid company.',
      });
      return;
    }

    const baseUrl = getBaseUrl();
    const rawToken = token || localStorage.getItem('token') || '';
    const authToken = rawToken
      ? rawToken.startsWith('Bearer ')
        ? rawToken
        : `Bearer ${rawToken}`
      : '';

    const paymentMode = getPaymentModeCode(paymentForm.paymentMethod);

    const custPayment: any = {
      Amount: received.toString(),
      CompanyID: companyIdStr,
      PMode: paymentMode,
      PaidOn: paymentForm.receivedOn,
      Reference: paymentForm.reference,
      Notes: paymentForm.notes,
      Bank: '',
      Account: '',
    };

    if (paymentForm.linkInvoice && paymentForm.selectedInvoiceId) {
      custPayment.InvoiceID = paymentForm.selectedInvoiceId;
    } else {
      const customerIdentifier = paymentForm.customerCode ?? paymentForm.customerId;
      if (!customerIdentifier) {
        toast({
          variant: 'destructive',
          description: 'Unable to determine customer for this payment',
        });
        return;
      }
      custPayment.CustomerID = String(customerIdentifier);
    }

    if (
      paymentForm.paymentMethod === 'Bank Transfer' &&
      paymentForm.bankName &&
      paymentForm.accountNumber
    ) {
      custPayment.Bank = paymentForm.bankName;
      custPayment.Account = paymentForm.accountNumber;
    }

    const payload = [custPayment];

    const endpoint = `${baseUrl}/api/CustPayment`;

    // Required console logging: endpoint and final JSON payload
    console.log('api/CustPayment');
    console.log(JSON.stringify(payload, null, 2));

    setPaymentSubmitting(true);

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          ...(authToken ? { Authorization: authToken } : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        console.error('CustPayment error status:', response.status, response.statusText);
        throw new Error('Failed to record payment');
      }

      const json = await response.json();
      console.log('🛰️ CustPayment response:', json);

      if (json.status === 0) {
        toast({ description: 'Payment recorded successfully' });
        setIsPaymentModalOpen(false);
        
        // Instead of hard refresh, call the refresh function with current parameters
        console.log('🔄 Refreshing customer ledger data after successful payment');
        setTimeout(() => {
          refreshCustomerLedgerData(
            paymentForm.customerCode,
            dateFilterEnabled ? dateRange.start : undefined,
            dateFilterEnabled ? dateRange.end : undefined
          );
        }, 500); // Wait 0.5 seconds to show success toast
      } else if (json.status === -1) {
        toast({
          variant: 'destructive',
          description: json.message || 'Error recording payment',
        });
      } else {
        toast({
          variant: 'destructive',
          description: json.message || 'Unexpected response from payment API',
        });
      }
      
      // Debug log to see what status we got
      console.log('Payment API response status:', json.status);
      
      // Ensure refresh happens even if API doesn't return expected status
      if (json.status !== 0) {
        console.log('Forcing refresh due to non-zero status');
        setIsPaymentModalOpen(false);
        setCustomersReloadKey((prev) => prev + 1);
      }
    } catch (error) {
      console.error('CustPayment fetch failed:', error);
      toast({ variant: 'destructive', description: 'Error recording payment' });
    } finally {
      setPaymentSubmitting(false);
    }
  };

  const actionButtonClass =
    'h-9 px-2 text-xs min-w-[110px] gap-2 justify-center shrink-0 sm:h-11 sm:px-4 sm:min-w-[140px] sm:text-base';

  const hasSelection = selectedCustomers.length > 0;

  const filteredCustomers = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return customers
      .map((customer) => {
        const customerMatches =
          !query ||
          customer.name.toLowerCase().includes(query) ||
          customer.buyer.toLowerCase().includes(query) ||
          customer.phone.toLowerCase().includes(query);

        const filteredInvoices = customer.invoices.filter((invoice) => {
          const invoiceMatches =
            !query ||
            invoice.invoiceNumber.toLowerCase().includes(query) ||
            invoice.companyName.toLowerCase().includes(query) ||
            invoice.location.toLowerCase().includes(query);

          const productMatches =
            !query ||
            invoice.products.some((product) => product.name.toLowerCase().includes(query));

          const dateMatches = matchesDateRange(
            invoice.invoiceDate,
            dateRange.start,
            dateRange.end,
            dateFilterEnabled,
          );

          return dateMatches && (customerMatches || invoiceMatches || productMatches);
        });

        const shouldKeep = customerMatches || filteredInvoices.length > 0;

        return shouldKeep ? { ...customer, invoices: filteredInvoices } : null;
      })
      .filter(Boolean) as CustomerLedger[];
  }, [searchTerm, dateRange, dateFilterEnabled, customers]);

  const visibleCustomers = useMemo(() => {
    if (pageSize === 'all') return filteredCustomers;
    const limit = parseInt(pageSize, 10);
    return filteredCustomers.slice(0, limit);
  }, [filteredCustomers, pageSize]);

  const toggleCustomer = (id: string, customerCode?: string) => {
    setExpandedCustomers((prev) => {
      const willExpand = !prev[id];
      if (willExpand && customerCode && !invoicesByCustomer[id]) {
        fetchCustomerInvoices(id, customerCode);
      }
      return { ...prev, [id]: willExpand };
    });

    setExpandedPayments((prev) => {
      const current = prev[id];
      if (current === undefined) {
        return { ...prev, [id]: true };
      }
      return prev;
    });
  };

  const toggleInvoice = (id: string) => {
    setExpandedInvoices((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const fetchCustomerInvoices = useCallback(
    async (customerId: string, customerCode: string) => {
      if (invoicesLoading[customerId]) return;
      const baseUrl = getBaseUrl();
      const rawToken = token || localStorage.getItem('token') || '';
      const authToken = rawToken
        ? rawToken.startsWith('Bearer ')
          ? rawToken
          : `Bearer ${rawToken}`
        : '';
      const start = dateRange.start || getTodayStr();
      const end = dateRange.end || getTodayStr();
      const fetchKey = (invoicesFetchRef.current[customerId] || 0) + 1;
      invoicesFetchRef.current[customerId] = fetchKey;

      const endpoint = `${baseUrl}/api/CustInvoicesPayments?StartDate=${encodeURIComponent(
        start,
      )}&EndDate=${encodeURIComponent(end)}&CustomerCode=${encodeURIComponent(
        customerCode,
      )}&Top=`;

      console.log('🛰️ CustInvoicesPayments request:', endpoint);
      setInvoicesLoading((prev) => ({ ...prev, [customerId]: true }));

      try {
        const response = await fetch(endpoint, {
          headers: {
            ...(authToken ? { Authorization: authToken } : {}),
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          console.error(
            'CustInvoicesPayments error status:',
            response.status,
            response.statusText,
          );
          throw new Error('Failed to fetch invoices/payments');
        }

        const json = await response.json();
        console.log('🛰️ CustInvoicesPayments response:', json);

        if (invoicesFetchRef.current[customerId] !== fetchKey) return;

        const apiInvoices = json.Invoices || [];
        
        const mappedInvoices: Invoice[] = apiInvoices.map((wrapper: any, idx: number) => {
          const inv = wrapper.Invoice || {};
          const lines = wrapper.InvoiceLineItems || [];
          const pays = wrapper.Payments || [];
          const invoiceId = inv.InvoiceId ?? idx;
          const invoiceNumber = inv.InvSeqNo ?? inv.InvoiceId ?? '';
          return {
            id: String(invoiceId),
            invoiceDate: inv.CreatedOn ? String(inv.CreatedOn).split('T')[0] : 'N/A',
            companyName: inv.CompanyName || inv.CustomerName || 'N/A',
            invoiceNumber: String(invoiceNumber),
            invoiceAmount: inv.InvoicedAmount ?? 0,
            location: inv.LocationName || inv.Location || 'N/A',
            profit: inv.Profit ?? 0,
            products: lines.map((li: any, liIdx: number) => ({
              id: String(li.Id ?? liIdx),
              name: li.ProductName || 'Product',
              unitCost: li.UnitCost ?? 0,
              qty: li.Quantity ?? 0,
              total:
                li.TotalCost ??
                ((li.UnitCost ?? 0) * (li.Quantity ?? 0)),
            })),
            payments: pays.map((p: any, pIdx: number) => {
              // Directly use InvSeqNo from payment data if available
              const paymentInvSeqNo = String(p.InvSeqNo ?? '');
              
              return {
                id: String(p.ID ?? pIdx),
                paidOn: p.PaidOn || '', // Keep full datetime
                paidAmount: p.Amount ?? 0,
                receiptNo: String(p.Reference ?? ''),
                type: getPaymentMethodFromStatus(p.Status ?? 0),
                invoiceNo: paymentInvSeqNo !== 'NA' && paymentInvSeqNo !== '' ? paymentInvSeqNo : '',
              };
            }),
          };
        });

        setInvoicesByCustomer((prev) => ({ ...prev, [customerId]: mappedInvoices }));
      } catch (error) {
        console.error('CustInvoicesPayments fetch failed:', error);
        setInvoicesByCustomer((prev) => ({ ...prev, [customerId]: [] }));
      } finally {
        setInvoicesLoading((prev) => ({ ...prev, [customerId]: false }));
      }
    },
    [dateRange.start, dateRange.end, token, invoicesLoading],
  );

  useEffect(() => {
    const currentKey = `${dateRange.start || ''}|${dateRange.end || ''}`;
    if (prevDateRangeKey.current === currentKey) return;
    prevDateRangeKey.current = currentKey;

    setDateFilterEnabled(!!(dateRange.start || dateRange.end));

    const expandedIds = Object.entries(expandedCustomers)
      .filter(([, isExpanded]) => isExpanded)
      .map(([id]) => id);

    setInvoicesByCustomer((prev) => (Object.keys(prev).length ? {} : prev));
    setExpandedInvoices((prev) => (Object.keys(prev).length ? {} : prev));

    expandedIds.forEach((customerId) => {
      const customer = customers.find((c) => c.id === customerId);
      const customerCode = customer?.customerCode || customerId;
      if (customerCode) {
        fetchCustomerInvoices(customerId, customerCode);
      }
    });
  }, [dateRange.start, dateRange.end, expandedCustomers, customers, fetchCustomerInvoices]);

  useEffect(() => {
    const controller = new AbortController();
    const currentFetchId = ++fetchIdRef.current;

    const run = async () => {
      const baseUrl = getBaseUrl();
      const rawToken = token || localStorage.getItem('token') || '';
      const authToken = rawToken ? (rawToken.startsWith('Bearer ') ? rawToken : `Bearer ${rawToken}`) : '';
      const limitParam = pageSize === 'all' ? 2000 : parseInt(pageSize, 10) || 25;
      const endpoint = `${baseUrl}/api/CustomerList?columnname=fname&direction=DESC&offset=0&limit=${limitParam}&searchstr=${encodeURIComponent(debouncedSearchTerm)}`;

      console.log('🛰️ CustomerList request:', endpoint);
      setIsLoadingCustomers(true);

      try {
        const response = await fetch(endpoint, {
          signal: controller.signal,
          headers: {
            ...(authToken ? { Authorization: authToken } : {}),
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          console.error('CustomerList error status:', response.status, response.statusText);
          throw new Error('Failed to fetch customers');
        }

        const json = await response.json();
        console.log('🛰️ CustomerList response:', json);

        // Ignore stale responses
        if (fetchIdRef.current !== currentFetchId) return;

        const mapped: CustomerLedger[] = (json.data || []).map((c: any, idx: number) => {
          // Overall mapping requirement:
          // Name  -> FName
          // Buyer -> LName
          const name = (c.FName || '').toString().trim() || 'N/A';
          const buyer = (c.LName || '').toString().trim() || 'N/A';

          return {
            id: String(c.Customercode ?? idx),
            customerCode: c.Customercode ? String(c.Customercode) : String(c.Customercode ?? idx),
            name,
            buyer,
            phone: c.Phone || 'N/A',
            openingBalance: c.OpeningBalance ?? 0,
            totalInvoiced: c['Total Invoiced Amount'] ?? 0,
            totalPaid: c['Total Paid Amount'] ?? 0,
            totalDue: c['Total Due Amount'] ?? 0,
            dueSinceDays: c['Number of days till today'] ?? 0,
            invoices: [],
          };
        });

        setCustomers(mapped);
      } catch (error) {
        if (controller.signal.aborted) {
          console.log('CustomerList fetch aborted');
        } else {
          console.error('CustomerList fetch failed:', error);
          setCustomers([]);
        }
      } finally {
        if (fetchIdRef.current === currentFetchId) {
          setIsLoadingCustomers(false);
        }
      }
    };

    run();

    return () => {
      controller.abort();
    };
  }, [pageSize, debouncedSearchTerm, token, customersReloadKey]);

  // Restore state from sessionStorage after refresh
  useEffect(() => {
    const savedState = sessionStorage.getItem('customerLedgerState');
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        console.log('🔄 Restoring state from sessionStorage:', state);
        
        if (state.searchTerm !== undefined) setSearchTerm(state.searchTerm);
        if (state.pageSize !== undefined) setPageSize(state.pageSize);
        if (state.dateRange !== undefined) setDateRange(state.dateRange);
        if (state.selectedCustomers !== undefined) setSelectedCustomers(state.selectedCustomers);
        if (state.dateFilterEnabled !== undefined) setDateFilterEnabled(state.dateFilterEnabled);
        
        // Clear the saved state after restoring
        sessionStorage.removeItem('customerLedgerState');
      } catch (error) {
        console.error('Failed to restore state from sessionStorage:', error);
      }
    }
  }, []);

  // Call CustInvoicesPayments API after component mounts/refreshes
  useEffect(() => {
    const callCustInvoicesPayments = async () => {
      const baseUrl = getBaseUrl();
      const rawToken = token || localStorage.getItem('token') || '';
      const authToken = rawToken ? (rawToken.startsWith('Bearer ') ? rawToken : `Bearer ${rawToken}`) : '';
      
      // Get the first selected customer code (if any)
      const selectedCustomerCode = selectedCustomers.length > 0 ? 
        customers.find(c => c.id === selectedCustomers[0])?.customerCode : null;
      
      if (selectedCustomerCode) {
        const params = new URLSearchParams({
          customerCode: selectedCustomerCode,
          startDate: dateRange.start || getTodayStr(),
          endDate: dateRange.end || getTodayStr(),
          searchTerm: debouncedSearchTerm
        });
        
        const endpoint = `${baseUrl}/api/CustInvoicesPayments?${params}`;
        console.log('🛰️ Calling CustInvoicesPayments after refresh:', endpoint);
        
        try {
          const response = await fetch(endpoint, {
            headers: {
              ...(authToken ? { Authorization: authToken } : {}),
              'Content-Type': 'application/json',
            },
          });
          
          if (response.ok) {
            const json = await response.json();
            console.log('🛰️ CustInvoicesPayments response:', json);
          }
        } catch (error) {
          console.error('CustInvoicesPayments fetch failed:', error);
        }
      }
    };
    
    callCustInvoicesPayments();
  }, [customersReloadKey, selectedCustomers, dateRange, debouncedSearchTerm, token, customers]);

  // Ensure selected customers remain valid after customer list refresh
  useEffect(() => {
    if (customers.length > 0 && selectedCustomers.length > 0) {
      const validCustomerIds = new Set(customers.map((c) => c.id));
      const validSelectedCustomers = selectedCustomers.filter((id) => validCustomerIds.has(id));
      if (validSelectedCustomers.length !== selectedCustomers.length) {
        setSelectedCustomers(validSelectedCustomers);
      }
    }
  }, [customers, selectedCustomers]);

  return (
    <div className="space-y-6 bg-white p-4 md:p-6 rounded-xl min-h-screen">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-9 px-3 text-gray-700 hover:text-gray-900"
              onClick={handleBackToReports}
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <div className="text-xl sm:text-2xl font-bold text-gray-900">Customer Ledger</div>
          </div>
          <div
            className="flex flex-nowrap md:flex-wrap gap-2 overflow-x-auto md:overflow-visible [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            <Button
              variant="default"
              className={cn(actionButtonClass, 'bg-blue-600 hover:bg-blue-700')}
              disabled={!hasSelection || !dateRange.start || !dateRange.end}
              onClick={handleCreateLedgerPDF}
            >
              <PlusCircle className="h-4 w-4" />
              Create Ledger
            </Button>
            {/* <Button
              variant="outline"
              className={actionButtonClass}
              disabled={!hasSelection}
              onClick={handleExportToExcel}
            >
              <FileSpreadsheet className="h-4 w-4" />
              Export To Excel
            </Button> */}
            <Button
              variant="outline"
              className={actionButtonClass}
              disabled={!hasSelection}
              onClick={openPaymentModal}
            >
              <IndianRupee className="h-4 w-4" />
              Part Payment
            </Button>
            <Button variant="outline" className={`${actionButtonClass} hidden`} disabled={!hasSelection}>
              <Printer className="h-4 w-4" />
              Print Invoice
            </Button>
            <Button 
              variant="outline" 
              className={actionButtonClass} 
              disabled={!hasSelection || selectedCustomers.length !== 1}
              onClick={() => {
                if (selectedCustomers.length === 1) {
                  handleDetailedLedgerPDF();
                }
              }}
            >
              <Download className="h-4 w-4" />
              Detailed Ledger
            </Button>
            <Button 
              variant="outline" 
              className={actionButtonClass} 
              onClick={handleOverallPDF}
              disabled={isLoadingCustomers}
            >
              Overall
            </Button>
          </div>
        </div>
      </div>

      <Card className="p-4 sm:p-5 space-y-3 shadow-sm border-gray-100">
        {isDesktop ? (
          <div className="flex flex-row items-end gap-3 lg:gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by customer, company, phone, invoice or product"
                  className="pl-9 h-11"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-end gap-3 lg:w-1/3">
              <Select value={pageSize} onValueChange={setPageSize}>
                <SelectTrigger className="w-full sm:w-32">
                  <SelectValue placeholder="List size" />
                </SelectTrigger>
                <SelectContent>
                  {sizeOptions.map((size) => (
                    <SelectItem key={size} value={size}>
                      {size === 'all' ? 'All' : size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-1">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-gray-600 flex items-center gap-1.5">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    Start Date
                  </label>
                  <Input
                    type="date"
                    value={dateRange.start || ''}
                    onChange={(e) => setDateRange((prev) => ({ ...prev, start: e.target.value }))}
                    className="h-11"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-gray-600 flex items-center gap-1.5">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    End Date
                  </label>
                  <Input
                    type="date"
                    value={dateRange.end || ''}
                    onChange={(e) => setDateRange((prev) => ({ ...prev, end: e.target.value }))}
                    className="h-11"
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-full text-gray-700 hover:text-gray-900 hover:bg-slate-100"
                onClick={() => setIsFilterCollapsed((prev) => !prev)}
                aria-label={isFilterCollapsed ? 'Show filters' : 'Hide filters'}
              >
                <ChevronRight
                  className={cn(
                    'h-4 w-4 transition-transform',
                    !isFilterCollapsed && 'rotate-90',
                  )}
                />
              </Button>

              <div className="relative flex-1 pr-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by customer, company, phone, invoice or product"
                  className="pl-9 h-11"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {!isFilterCollapsed && (
              <div className="flex flex-col gap-3 md:flex-row md:items-end md:gap-3">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-2 md:flex md:items-end md:gap-3 md:w-2/3">
                  <div className="space-y-1 md:flex-1">
                    <label className="text-xs font-semibold text-gray-600 flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5 text-gray-400" />
                      Start Date
                    </label>
                    <div className="relative">
                      <Input
                        type="date"
                        value={dateRange.start || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setDateRange((prev) => ({ ...prev, start: value }));
                          setDateFilterEnabled(!!value || !!dateRange.end);
                        }}
                        ref={mobileStartRef}
                        className="h-10 text-sm pr-10 appearance-none [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:appearance-none [&::-webkit-calendar-picker-indicator]:pointer-events-none"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (mobileStartRef.current?.showPicker) {
                            mobileStartRef.current.showPicker();
                          } else {
                            mobileStartRef.current?.focus();
                            mobileStartRef.current?.click();
                          }
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                        aria-label="Open start date picker"
                      >
                        <Calendar className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1 md:flex-1">
                    <label className="text-xs font-semibold text-gray-600 flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5 text-gray-400" />
                      End Date
                    </label>
                    <div className="relative">
                      <Input
                        type="date"
                        value={dateRange.end || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setDateRange((prev) => ({ ...prev, end: value }));
                          setDateFilterEnabled(!!value || !!dateRange.start);
                        }}
                        ref={mobileEndRef}
                        className="h-10 text-sm pr-10 appearance-none [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:appearance-none [&::-webkit-calendar-picker-indicator]:pointer-events-none"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (mobileEndRef.current?.showPicker) {
                            mobileEndRef.current.showPicker();
                          } else {
                            mobileEndRef.current?.focus();
                            mobileEndRef.current?.click();
                          }
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                        aria-label="Open end date picker"
                      >
                        <Calendar className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="md:w-1/3">
                  <Select value={pageSize} onValueChange={setPageSize}>
                    <SelectTrigger className="w-full sm:w-40 h-10 text-sm md:w-full">
                      <SelectValue placeholder="List size" />
                    </SelectTrigger>
                    <SelectContent>
                      {sizeOptions.map((size) => (
                        <SelectItem key={size} value={size}>
                          {size === 'all' ? 'All' : size}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      <div className="space-y-3">
        <div className="hidden lg:grid grid-cols-[32px,32px,60px,1.4fr,1fr,1fr,1fr,1fr,1fr,1fr,1fr] items-center gap-3 px-3 sm:px-4 text-xs font-semibold text-gray-500 mb-2">
          <div />
          <button
            type="button"
            onClick={() => {
              const allIds = visibleCustomers.map(c => c.id);
              const allSelected = allIds.length > 0 && allIds.every(id => selectedCustomers.includes(id));
              if (allSelected) {
                // Deselect all visible customers
                setSelectedCustomers(prev => prev.filter(id => !allIds.includes(id)));
              } else {
                // Select all visible customers
                setSelectedCustomers(prev => {
                  const newIds = allIds.filter(id => !prev.includes(id));
                  return [...prev, ...newIds];
                });
              }
            }}
            className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline text-left"
          >
            All
          </button>
          <div className="text-center">S.No</div>
          <div>Name</div>
          <div>Company</div>
          <div>Phone</div>
          <div className="text-right">Opening Balance</div>
          <div className="text-right">Total Invoiced</div>
          <div className="text-right">Total Paid</div>
          <div className="text-right">Total Due</div>
          <div className="text-right">Due Since Days</div>
        </div>

        {visibleCustomers.length === 0 && (
          <Card className="p-6 text-center text-gray-500 border-dashed border-2 border-gray-200">
            No customers found for the selected filters.
          </Card>
        )}

        {visibleCustomers.length > 0 && (
          <div className="lg:hidden mb-2 flex items-center gap-3 px-3 sm:px-4">
            <button
              type="button"
              onClick={() => {
                const allIds = visibleCustomers.map(c => c.id);
                const allSelected = allIds.length > 0 && allIds.every(id => selectedCustomers.includes(id));
                if (allSelected) {
                  // Deselect all visible customers
                  setSelectedCustomers(prev => prev.filter(id => !allIds.includes(id)));
                } else {
                  // Select all visible customers
                  setSelectedCustomers(prev => {
                    const newIds = allIds.filter(id => !prev.includes(id));
                    return [...prev, ...newIds];
                  });
                }
              }}
              className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
            >
              All
            </button>
          </div>
        )}

        <div className="space-y-3">
          {visibleCustomers.map((customer, index) => {
            const customerWithSerial = {
              ...customer,
              serialNumber: index + 1
            };
            return (
            <CustomerRow
              key={customer.id}
              customer={customerWithSerial}
              expanded={!!expandedCustomers[customer.id]}
              onToggle={() => toggleCustomer(customer.id, customer.customerCode || customer.id)}
              expandedInvoices={expandedInvoices}
              toggleInvoice={toggleInvoice}
              paymentsExpanded={expandedPayments[customer.id] ?? true}
              togglePayments={() =>
                setExpandedPayments((prev) => ({
                  ...prev,
                  [customer.id]: !(prev[customer.id] ?? true),
                }))
              }
              onOpeningBalanceEdit={openObModal}
              selected={selectedCustomers.includes(customer.id)}
              onSelectToggle={() =>
                setSelectedCustomers((prev) =>
                  prev.includes(customer.id)
                    ? prev.filter((id) => id !== customer.id)
                    : [...prev, customer.id],
                )
              }
              invoices={invoicesByCustomer[customer.id] ?? customer.invoices ?? []}
              invoicesLoading={!!invoicesLoading[customer.id]}
            />
            );
          })}
        </div>
      </div>
      <Dialog
        open={isObModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            closeObModal();
          } else {
            setIsObModalOpen(true);
          }
        }}
      >
        <DialogContent className="sm:max-w-md p-5 sm:p-6 rounded-xl shadow-2xl data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95">
          <DialogHeader className="flex flex-row items-start justify-between space-y-0">
            <DialogTitle className="text-lg font-semibold text-gray-900">Update Opening Balance</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="ob-input" className="text-sm font-medium text-gray-700">
                Opening Balance
              </Label>
              <Input
                id="ob-input"
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                value={obForm.openingBalance ?? ''}
                onChange={(e) => setObForm((prev) => ({ ...prev, openingBalance: e.target.value }))}
                className="h-11"
                placeholder="0"
              />
            </div>
            <div className="flex items-center justify-start text-sm text-gray-500">
              <span className="truncate">Customer: {obForm.customerName || 'N/A'}</span>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button
              className="w-full sm:w-auto min-w-[140px]"
              onClick={handleSubmitOb}
              disabled={!obForm.openingBalance || obSubmitting}
            >
              {obSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isPaymentModalOpen}
        onOpenChange={(open) => {
          if (!open) closePaymentModal();
          else setIsPaymentModalOpen(true);
        }}
      >
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto p-5 sm:p-6 rounded-xl shadow-2xl data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-gray-900">
              Payment Received
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-gray-700">Customer Name</Label>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900">
                {paymentForm.customerName || 'N/A'}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-gray-700">Company</Label>
              <Select
                value={paymentForm.companySelected || ''}
                onValueChange={(val) =>
                  setPaymentForm((prev) => ({
                    ...prev,
                    companySelected: val,
                    companySearch: val,
                  }))
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  {companyOptions.map((comp) => (
                    <SelectItem key={comp} value={comp}>
                      {comp}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-gray-700">Total Due</Label>
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-amber-700">
                  {currency.format(paymentForm.totalDue || 0)}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-gray-700">Payment Received</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={paymentForm.paymentReceived}
                  onChange={(e) =>
                    setPaymentForm((prev) => ({ ...prev, paymentReceived: e.target.value }))
                  }
                  placeholder="0"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-gray-700">Remaining Due</Label>
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-900">
                  {currency.format(remainingDue)}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-gray-700">Received On</Label>
                <Input
                  type="date"
                  value={paymentForm.receivedOn}
                  onChange={(e) =>
                    setPaymentForm((prev) => ({ ...prev, receivedOn: e.target.value }))
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-gray-700">Reference</Label>
                <Input
                  placeholder="Enter reference"
                  value={paymentForm.reference}
                  onChange={(e) =>
                    setPaymentForm((prev) => ({ ...prev, reference: e.target.value }))
                  }
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={paymentForm.linkInvoice}
                    onChange={async (e) => {
                      const checked = e.target.checked;
                      setPaymentForm((prev) => ({
                        ...prev,
                        linkInvoice: checked,
                        invoiceSearch: '',
                        invoiceSelected: '',
                        selectedInvoiceId: undefined,
                        selectedInvoiceCompanyId: undefined,
                      }));

                      if (checked && paymentForm.customerCode) {
                        await fetchCustomerDueInvoices(paymentForm.customerCode);
                      } else if (!checked) {
                        setInvoiceOptions([]);
                      }
                    }}
                  />
                  Link to Invoice
                </Label>
                <Input
                  placeholder="Search invoice"
                  value={paymentForm.invoiceSearch}
                  disabled={!paymentForm.linkInvoice}
                  list="invoice-options"
                  onChange={(e) => {
                    const value = e.target.value;
                    const match = invoiceOptions.find((opt) => opt.invSeqNo === value);
                    setPaymentForm((prev) => ({
                      ...prev,
                      invoiceSearch: value,
                      invoiceSelected: value,
                      selectedInvoiceId: match?.invoiceId,
                      selectedInvoiceCompanyId: match?.companyId,
                    }));
                  }}
                  className={cn(!paymentForm.linkInvoice && 'pointer-events-none opacity-60')}
                />
                <datalist id="invoice-options">
                  {filteredInvoices.map((inv) => (
                    <option key={inv} value={inv} />
                  ))}
                </datalist>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-gray-700">Notes</Label>
              <Textarea
                rows={4}
                placeholder="Add notes"
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm((prev) => ({ ...prev, notes: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Payment Method</Label>
              <div className="flex flex-wrap gap-2">
                {['Cash', 'Card', 'Wallet', 'Cheque'].map(
                  (method) => (
                    <Button
                      key={method}
                      type="button"
                      variant={paymentForm.paymentMethod === method ? 'default' : 'outline'}
                      className={cn(
                        'h-9 text-sm',
                        method === 'Bank Transfer' ? 'min-w-[160px] flex-1 sm:flex-none' : 'min-w-[100px] flex-1 sm:flex-none',
                      )}
                      onClick={() => setPaymentForm((prev) => ({ ...prev, paymentMethod: method }))}
                    >
                      {method}
                    </Button>
                  ),
                )}
              </div>

              {paymentForm.paymentMethod === 'Bank Transfer' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-gray-700">Bank Name</Label>
                    <Select
                      key={paymentForm.bankName || 'all-banks'}
                      value={paymentForm.bankName || ''}
                      onValueChange={(val) => updateAccountFromBank(val)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select bank" />
                      </SelectTrigger>
                      <SelectContent>
                        {bankData.map((b) => (
                          <SelectItem key={b.bank} value={b.bank}>
                            {b.bank}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-gray-700">Account Number</Label>
                    <Select
                      key={paymentForm.accountNumber || 'all-accounts'}
                      value={paymentForm.accountNumber || ''}
                      onValueChange={(val) => updateBankFromAccount(val)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select account" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableAccounts.length > 0 ? (
                          availableAccounts.map((item) => (
                            <SelectItem key={`${item.bank}-${item.account}`} value={item.account}>
                              {item.account}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="no-accounts" disabled>
                            No accounts available
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="pt-4 flex items-center justify-end gap-3">
            <Button variant="outline" onClick={closePaymentModal} disabled={paymentSubmitting}>
              Close
            </Button>
            <Button
              onClick={handlePaymentSubmit}
              disabled={paymentSubmitting}
              className="min-w-[140px]"
            >
              {paymentSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Received
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerLedgerTab;

